import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, posApi, promotionsApi } from '@/services/api';
import { useCartStore, MAX_HELD_CARTS, type CartSnapshot } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useShiftStore } from '@/store/shiftStore';
import { getActiveScaleSettings, isScaleBarcodeEnabled } from '@/store/scaleStore';
import { isCashierLevel, type CustomerItemPrice } from '@/types';
import ShiftGate from '@/components/pos/ShiftGate';
import SyncModeBanner from '@/components/pos/SyncModeBanner';
import { completeSale, type CompleteSaleInput, createCustomerRouted, getCustomersForPos, getItemMinRateRouted, getItemsForPos, getLocalReceiptData, isLocalFirstMode, companyDb } from '@/offline';
import { getInvoiceDetailsRouted } from '@/offline/posModuleRouter';
import { useSyncStore } from '@/store/syncStore';
import { usePrinterStore } from '@/store/printerStore';
import { WALK_IN_CUSTOMER_ID } from '@/constants/customers';
import { getDefaultCategoryColor, POS_CATEGORY_SELECTED_CLASS } from '@/constants/posCategoryIcons';
import { tryAutoConnectPrinter, primePrinterForAutoPrint } from '@/hooks/usePrinterConnection';
import { silentPrintReceipt } from '@/utils/silentPrint';

import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import PaymentModal from '@/components/pos/PaymentModal';
import ServiceCheckoutModal, { type ServiceJobDraft } from '@/components/pos/ServiceCheckoutModal';
import PendingInvoicesModal from '@/components/pos/PendingInvoicesModal';
import TodaySalesModal from '@/components/pos/TodaySalesModal';
import Receipt from '@/components/pos/Receipt';
import CustomerReturnModal from '@/components/pos/CustomerReturnModal';
import LockScreenModal from '@/components/pos/LockScreenModal';
import AddExpenseModal from '@/components/pos/AddExpenseModal';
import VariantSelectModal from '@/components/pos/VariantSelectModal';
import WeightEntryModal from '@/components/pos/WeightEntryModal';
import SerialSelectModal from '@/components/pos/SerialSelectModal';
import AgeVerificationModal from '@/components/pos/AgeVerificationModal';
import CartLineNoteModal, { type CartLineNoteMode } from '@/components/pos/CartLineNoteModal';
import PosDetailCardView from '@/components/pos/PosDetailCardView';
import PosImageGridView from '@/components/pos/PosImageGridView';
import PosCompactListView from '@/components/pos/PosCompactListView';
import PosQuickAccessStrip from '@/components/pos/PosQuickAccessStrip';
import PosMobileCheckoutBar from '@/components/pos/PosMobileCheckoutBar';
import PosMobileOverflowMenu from '@/components/pos/PosMobileOverflowMenu';
import PosCategoryBar from '@/components/pos/PosCategoryBar';
import PosCategoryPanel from '@/components/pos/PosCategoryPanel';
import CategoryIcon from '@/components/pos/CategoryIcon';
import HeldCartsPanel, { HeldCartsToggleButton } from '@/components/pos/HeldCartsPanel';
import ManagerOverrideModal from '@/components/pos/ManagerOverrideModal';
import UnknownBarcodeModal, { type UnknownBarcodeSavePayload } from '@/components/pos/UnknownBarcodeModal';
import PosShortcutsHelpModal from '@/components/pos/PosShortcutsHelpModal';
import ShortcutBadge from '@/components/pos/ShortcutBadge';
import type { PaymentModalHandle } from '@/components/pos/PaymentModal';
import { useFavoriteIds, useFavoritesStore } from '@/store/favoritesStore';
import { usePosUiStore, type PosLayoutMode, type PosShortcutAction } from '@/store/posUiStore';
import { formatShortcutKey } from '@/data/posShortcuts';
import { usePosKeyboardShortcuts } from '@/hooks/usePosKeyboardShortcuts';
import { useIsMobilePos } from '@/hooks/useIsMobilePos';
import { useCustomerDisplayBridge, broadcastThankYou } from '@/hooks/useCustomerDisplayBridge';
import { openCustomerDisplayWindow } from '@/utils/customerDisplayChannel';
import { openCashDrawer } from '@/utils/thermalPrinterUtils';
import { playBellSound } from '@/utils/soundEffects';
import {
  Package, Wrench, Search, ShoppingCart, Trash2, Minus, Plus,
  User, CreditCard, Barcode, RotateCcw, Menu, X, Clock, Camera, FileText, ChevronDown, Pause, Layers, Keyboard, Monitor, Flame, Edit2, Hash, LayoutGrid, MessageSquare, Pencil,
  Truck, Store, MapPin, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import GeneralItemModal from '@/components/pos/GeneralItemModal';
import DeliveryDetailsModal from '@/components/pos/DeliveryDetailsModal';

import CameraScannerModal from '@/components/pos/CameraScannerModal';
import type { POSItem, ItemVariant, PosSettings, CartItem, BarcodeScanResult, PackageLimits, POSCustomField, ReceiptData, POSCategory } from '@/types';
import {
  normalizePhone,
  sanitizePhoneInput,
  validatePhone,
} from '@/utils/mobileValidation';
import {
  COUNTRIES,
  getCountry,
  getRegionsForCountry,
  isEuOrUk,
  isPakistan,
} from '@/data/locales';
import UkPostcodeLookup from '@/components/common/UkPostcodeLookup';
import { setLocaleContext, formatMoney, getCurrencySymbol, calcLineAmount, roundMoney } from '@/utils/currency';
import {
  canAddExpenses,
  canBypassStaffLimits,
  canEditCartRate,
  discountExceedsCap,
  getEffectiveDiscountCap,
  getMaxDiscountAmount,
} from '@/utils/staffPermissions';
import { activeBanners, evaluateCart, type PromotionCampaign } from '@/utils/promotions';

function holdCartTotal(snap: CartSnapshot, pricesIncludeTax = false) {
  const sub = roundMoney(snap.items.reduce((s, i) => s + i.amount, 0));
  if (pricesIncludeTax) return roundMoney(sub - (snap.discount || 0));
  return roundMoney(sub - (snap.discount || 0) + (snap.tax || 0));
}

function formatHoldTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

type ViewMode = 'products' | 'services';

/** Category pill value for hot-selling items (not a real POS Category). */
const RECENT_CATEGORY = '__recent__';

function isGeneralItemProduct(item?: { item_code?: string; item_name?: string; name?: string; item?: string }) {
  if (!item) return false;
  const code = (item.item_code || '').trim().toLowerCase();
  const name = (item.item_name || '').trim().toLowerCase();
  const docName = (item.name || '').trim().toLowerCase();
  const it = (item.item || '').trim().toLowerCase();
  return code === 'general item' || name === 'general item' || docName === 'general item' || it === 'general item';
}

function getCartQtyForItem(
  items: CartItem[],
  itemName: string,
  excludeIdx?: number,
  variant?: string,
) {
  return items.reduce((sum, line, idx) => {
    if (line.item_type !== 'Product' || line.item !== itemName) return sum;
    if (variant !== undefined && (line.variant || '') !== variant) return sum;
    if (excludeIdx !== undefined && idx === excludeIdx) return sum;
    return sum + line.quantity;
  }, 0);
}

function canAddProductQty(
  item: POSItem,
  cartItems: CartItem[],
  allowOos: boolean,
  addQty = 1,
  excludeIdx?: number,
  variant?: ItemVariant,
) {
  if (item.item_type !== 'Product' || allowOos) return true;
  const stock = variant ? (variant.stock_quantity ?? 0) : (item.stock_quantity ?? 0);
  const inCart = getCartQtyForItem(
    cartItems,
    item.name,
    excludeIdx,
    variant?.name,
  );
  return inCart + addQty <= stock;
}

interface BackendCustomer {
  name: string;
  customer_name: string;
  mobile: string | null;
  email: string | null;
  city: string | null;
  address: string | null;
  postal_code?: string | null;
  is_walk_in: number;
  outstanding_balance: number;
  creation: string;
  is_default?: number;
  customer_type?: string;
  custom_prices?: CustomerItemPrice[];
}

export interface PosSelectedCustomer {
  id: string;
  name: string;
  phone: string;
  customer_type?: string;
  is_default?: number;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  is_walk_in?: number;
  custom_prices?: CustomerItemPrice[];
}

const EMPTY_POS_CUSTOMERS: BackendCustomer[] = [];

interface CustomerFieldsProps {
  customers: BackendCustomer[];
  value: PosSelectedCustomer;
  onChange: (customer: PosSelectedCustomer) => void;
  onCreateCustomer?: (name: string, phone: string) => void;
  isCreatingCustomer?: boolean;
}

// 🎯 ISOLATED SUB-COMPONENT FOR CUSTOMER FIELDS
function CustomerFields({ customers, value, onChange, onCreateCustomer }: CustomerFieldsProps) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: searchResults } = useQuery({
    queryKey: ['posCustomersSearch', posSyncMode, catalogSyncedAt, debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch) return [];
      if (isLocalFirstMode()) {
        return getCustomersForPos({ search: debouncedSearch, limit: 50 });
      }
      const res = await posApi.getCustomers({ search: debouncedSearch, limit: 50 });
      return res.data.message?.customers || res.data.message || [];
    },
    enabled: !!debouncedSearch,
  });



  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredCustomers = useMemo(() => {
    let list = [];
    if (debouncedSearch && searchResults) {
      list = [...searchResults] as BackendCustomer[];
    } else {
      const term = search.toLowerCase();
      list = customers.filter(c =>
        c.customer_name.toLowerCase().includes(term) || (c.mobile && c.mobile.includes(term))
      );
    }
    // Sort so that is_default === 1 is always at the top, then alphabetically
    list.sort((a, b) => {
      const aDef = a.is_default === 1;
      const bDef = b.is_default === 1;
      if (aDef && !bDef) return -1;
      if (!aDef && bDef) return 1;
      return a.customer_name.localeCompare(b.customer_name);
    });
    return list;
  }, [customers, search, debouncedSearch, searchResults]);

  const handleSelect = (c: BackendCustomer | null) => {
    if (c) {
      onChange({
        id: c.name,
        name: c.customer_name,
        phone: c.mobile || '',
        customer_type: c.customer_type,
        is_default: c.is_default,
        address: c.address || '',
        postal_code: c.postal_code || '',
        city: c.city || '',
        is_walk_in: c.is_walk_in,
        custom_prices: c.custom_prices || [],
      });
    } else {
      onChange({ id: '', name: WALK_IN_CUSTOMER_ID, phone: '', address: '', postal_code: '', city: '', is_walk_in: 1, custom_prices: [] });
    }
    setShowDropdown(false);
    setSearch('');
  };

  const selectedCustomerType = value.customer_type || 'Retailer';
  const isSelectedDefault = value.is_default === 1;

  const getCustomerTypeBadge = (type?: string, isDefaultCustomer?: boolean) => {
    const val = type || 'Retailer';
    let badge = null;
    if (val === 'Wholesale') {
      badge = (
        <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 shrink-0">
          {t('pos.wholesale')}
        </span>
      );
    } else if (val === 'Distributor' || val === 'Distributer') {
      badge = (
        <span className="text-[9px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 shrink-0">
          {t('pos.distributor')}
        </span>
      );
    } else {
      badge = (
        <span className="text-[9px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 shrink-0">
          {t('pos.retailer')}
        </span>
      );
    }

    if (isDefaultCustomer) {
      return (
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300">
            {t('common.default')}
          </span>
          {badge}
        </div>
      );
    }
    return badge;
  };

  const getCustomerTypeBadgeLarge = (type?: string, isDefaultCustomer?: boolean) => {
    const val = type || 'Retailer';
    let badge = null;
    if (val === 'Wholesale') {
      badge = (
        <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100 shrink-0">
          {t('pos.wholesale')}
        </span>
      );
    } else if (val === 'Distributor' || val === 'Distributer') {
      badge = (
        <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100 shrink-0">
          {t('pos.distributor')}
        </span>
      );
    } else {
      badge = (
        <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 shrink-0">
          {t('pos.retailer')}
        </span>
      );
    }

    if (isDefaultCustomer) {
      return (
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full border border-emerald-200">
            {t('common.default')}
          </span>
          {badge}
        </div>
      );
    }
    return badge;
  };

  return (
    <div className="space-y-2" ref={dropdownRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(prev => !prev)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 bg-white rounded-lg text-sm hover:bg-gray-50 transition-colors shadow-sm cursor-pointer"
        >
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="font-semibold text-gray-900 truncate">
              {value.id ? value.name : t('pos.walkIn')}
            </span>
            {value.id && getCustomerTypeBadgeLarge(selectedCustomerType, isSelectedDefault)}
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        </button>

        {showDropdown && (
          <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl border border-gray-100 z-[100] overflow-hidden flex flex-col max-h-80">
            <div className="p-2 border-b border-gray-100 flex items-center gap-2 shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-primary-500"
                  placeholder={t('pos.searchCustomer')}
                  autoFocus
                />
              </div>
              {onCreateCustomer && (
                <button
                  type="button"
                  onClick={() => {
                    onCreateCustomer(search, '');
                    setShowDropdown(false);
                  }}
                  className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1 shrink-0 cursor-pointer"
                  title="Add new customer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('common.add')}
                </button>
              )}
            </div>

            <div className="overflow-y-auto flex-1 p-1 space-y-0.5 max-h-56">

              {filteredCustomers.map((c) => {
                const isSelected = value.id === c.name;
                const isDefault = c.is_default === 1;
                let bgClass = "text-gray-700 border border-transparent";
                if (isSelected) {
                  bgClass = "bg-primary-50 text-primary-700 font-semibold border border-primary-100";
                } else if (isDefault) {
                  bgClass = "bg-emerald-50/30 text-emerald-950 border border-emerald-100/60 hover:bg-emerald-100/30";
                }

                return (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => handleSelect(c)}
                    className={`w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-gray-50 flex flex-col gap-0.5 cursor-pointer ${bgClass}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="truncate font-medium">{c.customer_name}</span>
                      {getCustomerTypeBadge(c.customer_type, isDefault)}
                    </div>
                    {c.mobile && (
                      <span className="text-[10px] text-gray-400 font-mono">
                        {c.mobile}
                      </span>
                    )}
                  </button>
                );
              })}

              {filteredCustomers.length === 0 && (
                <div className="p-4 text-center text-xs text-gray-400">
                  {t('pos.noCustomers')}
                </div>
              )}
            </div>

            {search.trim() !== '' && onCreateCustomer && (
              <div className="p-1.5 bg-gray-50 border-t border-gray-100 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    onCreateCustomer(search, '');
                    setShowDropdown(false);
                  }}
                  className="w-full py-1.5 text-center text-xs text-primary-600 hover:text-primary-700 font-medium hover:bg-white rounded-lg border border-dashed border-primary-200 transition-all flex items-center justify-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('pos.createCustomer', { name: search })}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

import { useLocation } from 'react-router-dom';

export default function POSPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const isQuotationMode = location.state?.isQuotationMode === true;
  const [quoteExpiryDate, setQuoteExpiryDate] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });

  const [viewMode, setViewMode] = useState<ViewMode>('products');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const mobileCategoryDropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [barcodeScan, setBarcodeScan] = useState('');
  const [scanError, setScanError] = useState('');
  const [unknownBarcode, setUnknownBarcode] = useState<string | null>(null);
  const [scanPending, setScanPending] = useState(false);
  const desktopBarcodeInputRef = useRef<HTMLInputElement>(null);
  const mobileBarcodeInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showManagerOverride, setShowManagerOverride] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState('Manager approval required.');
  const [managerApprover, setManagerApprover] = useState<string | null>(null);
  const pendingDiscountRef = useRef<number | null>(null);
  const pendingDiscountInputRef = useRef<string>('');
  const pendingRateIdxRef = useRef<number | null>(null);
  const pendingRateRef = useRef<number | null>(null);
  const paymentModalRef = useRef<PaymentModalHandle>(null);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showServiceCheckout, setShowServiceCheckout] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isCartMobileOpen, setIsCartMobileOpen] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnInvoiceId, setReturnInvoiceId] = useState<string | undefined>();
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showTodaySalesModal, setShowTodaySalesModal] = useState(false);
  const [selectedCartLineIdx, setSelectedCartLineIdx] = useState<number | null>(null);
  const prevCartLenRef = useRef(0);
  const cartLineRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [returnNotice, setReturnNotice] = useState('');
  const [lastInvoice, setLastInvoice] = useState('');
  const [receiptPreview, setReceiptPreview] = useState<Record<string, unknown> | null>(null);
  const [saleError, setSaleError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [variantModalItemName, setVariantModalItemName] = useState<string | null>(null);
  const [editingGeneralItemIdx, setEditingGeneralItemIdx] = useState<number | null>(null);
  const [lineNoteEdit, setLineNoteEdit] = useState<{ index: number; mode: CartLineNoteMode } | null>(null);
  const [weightPending, setWeightPending] = useState<{
    item: POSItem;
    variant?: ItemVariant;
    rate: number;
  } | null>(null);
  const [serialPending, setSerialPending] = useState<{
    item: POSItem;
    variant?: ItemVariant;
  } | null>(null);
  const [serialModalItemIdx, setSerialModalItemIdx] = useState<number | null>(null);
  const [agePending, setAgePending] = useState<{
    item: POSItem;
    variant?: ItemVariant;
    qtyOverride?: number;
    serialInfo?: { serial?: string; imei?: string; mac_address?: string };
    scanMeta?: {
      weight_source?: string;
      scanned_scale_barcode?: string;
      scale_plu?: string;
      scale_weight?: number;
    };
  } | null>(null);
  const [verifiedCustomerBirthYear, setVerifiedCustomerBirthYear] = useState<number | null>(null);

  const [discountType, setDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [discountInput, setDiscountInput] = useState<number | string>('');

  const [taxType, setTaxType] = useState<'amount' | 'percentage'>('amount');
  const [taxInput, setTaxInput] = useState<number | string>('');

  const [selectedCustomer, setSelectedCustomer] = useState<PosSelectedCustomer>({ id: '', name: '', phone: '' });
  const hasInitializedCustomer = useRef(false);
  const [showHeldCarts, setShowHeldCarts] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  /** BT connect started on Complete Sale click (preserves user gesture). */
  const printerPrimeRef = useRef<Promise<void>>(Promise.resolve());

  const applyCustomerDeliveryAddress = async (cust: PosSelectedCustomer) => {
    const isWalkIn = !cust.id || cust.name === WALK_IN_CUSTOMER_ID || Boolean(cust.is_walk_in);
    if (isWalkIn) {
      cart.setDeliveryInfo({
        address: '',
        postal_code: '',
        recipient_name: cust.name && cust.name !== WALK_IN_CUSTOMER_ID ? cust.name : '',
        phone: cust.phone || '',
      });
      setShowDeliveryModal(true);
      return;
    }

    let address = (cust.address || '').trim();
    let postalCode = (cust.postal_code || '').trim();
    let phone = (cust.phone || '').trim();
    let recipientName = (cust.name || '').trim();

    if (!address || !postalCode) {
      const match = customers.find((c) => c.name === cust.id);
      if (match) {
        if (!address && match.address) address = match.address.trim();
        if (!postalCode && match.postal_code) postalCode = match.postal_code.trim();
        if (!phone && match.mobile) phone = match.mobile.trim();
        if (!recipientName && match.customer_name) recipientName = match.customer_name.trim();
      }
    }

    if (!address || !postalCode) {
      try {
        const res = await posApi.getCustomers({ search: cust.id, limit: 1 });
        const list = res.data?.message?.customers || res.data?.message || [];
        const match = Array.isArray(list) ? list.find((c: any) => c.name === cust.id) : null;
        if (match) {
          if (!address && match.address) address = String(match.address).trim();
          if (!postalCode && match.postal_code) postalCode = String(match.postal_code).trim();
          if (!phone && match.mobile) phone = String(match.mobile).trim();
          if (!recipientName && match.customer_name) recipientName = String(match.customer_name).trim();
        }
      } catch (err) {
        // Silently continue
      }
    }

    const hasCompleteAddress = Boolean(address && postalCode);

    cart.setDeliveryInfo({
      address: address || '',
      postal_code: postalCode || '',
      recipient_name: recipientName || '',
      phone: phone || '',
    });

    if (hasCompleteAddress && posSettings?.delivery_pricing_mode !== 'runtime_charge') {
      setShowDeliveryModal(false);
      toast.success(`Delivery address loaded for ${recipientName || 'customer'}`);
    } else {
      setShowDeliveryModal(true);
    }
  };

  const handleFulfillmentChange = (type: 'Pickup' | 'Delivery') => {
    if (!posSettings?.enable_delivery && type === 'Delivery') return;
    cart.setFulfillmentType(type, posSettings?.delivery_pricing_mode);
    if (type === 'Delivery') {
      applyCustomerDeliveryAddress(selectedCustomer);
    }
  };

  const handleCustomerChange = (cust: PosSelectedCustomer) => {
    setSelectedCustomer(cust);
    if (cart.fulfillmentType === 'Delivery') {
      applyCustomerDeliveryAddress(cust);
    }
  };

  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomerFormData, setNewCustomerFormData] = useState({
    customer_name: '', mobile: '', country: 'PK', province: '', postal_code: '', city: '', address: '',
  });
  const [newCustomerErrors, setNewCustomerErrors] = useState<{ name?: string; mobile?: string }>({});
  const [newCustomerPostcodeError, setNewCustomerPostcodeError] = useState('');

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => { const res = await authApi.getCompanyProfile(); return res.data.message; },
  });
  const defaultCountry = profile?.country || 'GB';

  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await posApi.getCities();
      return res.data.message as { name: string; city: string }[];
    },
  });

  const qc = useQueryClient();
  const cart = useCartStore();

  useEffect(() => {
    if (cart.items.length === 0) {
      setVerifiedCustomerBirthYear(null);
    }
  }, [cart.items.length]);

  const createCustomerMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => createCustomerRouted(data),
    onSuccess: (newCustomer: any) => {
      qc.invalidateQueries({ queryKey: ['posCustomers'] });
      if (newCustomer) {
        const newCustObj: PosSelectedCustomer = {
          id: newCustomer.name,
          name: newCustomer.customer_name,
          phone: newCustomer.mobile || '',
          customer_type: newCustomer.customer_type,
          is_default: newCustomer.is_default,
          address: newCustomer.address,
          postal_code: newCustomer.postal_code,
          city: newCustomer.city,
          is_walk_in: newCustomer.is_walk_in,
        };
        setSelectedCustomer(newCustObj);
        setShowAddCustomerModal(false);
        setNewCustomerErrors({});
        if (cart.fulfillmentType === 'Delivery') {
          applyCustomerDeliveryAddress(newCustObj);
        }
      }
    },
    onError: (err: any) => {
      let msg = err.message || "Failed to save customer";
      if (err.response?.data?._server_messages) {
        try {
          msg = JSON.parse(JSON.parse(err.response.data._server_messages)[0]).message;
        } catch (e) { }
      }
      setNewCustomerErrors({ mobile: msg });
    }
  });

  const handleCreateCustomer = (name: string, phone: string) => {
    setNewCustomerFormData({
      customer_name: name,
      mobile: sanitizePhoneInput(phone, defaultCountry),
      country: defaultCountry,
      province: '',
      postal_code: '',
      city: '',
      address: '',
    });
    setNewCustomerErrors({});
    setNewCustomerPostcodeError('');
    setShowAddCustomerModal(true);
  };
  const addItemAction = useCartStore((state) => state.addItem);
  const { role, company, user, permissions } = useAuthStore();
  const userId = user?.name || '';
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  const favoriteIds = useFavoriteIds(userId);
  const setFavoritesRemote = useFavoritesStore((s) => s.setFavorites);
  const handleToggleFavorite = useCallback((itemName: string) => {
    toggleFavorite(userId, itemName);
    const next = useFavoritesStore.getState().getFavorites(userId);
    void posApi.setUserFavorites(next);
  }, [toggleFavorite, userId]);
  const touchModeOverride = usePosUiStore((s) => s.touchMode);
  const layoutOverride = usePosUiStore((s) => s.layoutOverride);
  const showFavoritesStrip = usePosUiStore((s) => s.showFavoritesStrip);
  const setShowFavoritesStrip = usePosUiStore((s) => s.setShowFavoritesStrip);
  const setLayoutOverride = usePosUiStore((s) => s.setLayoutOverride);
  const shortcutBindings = usePosUiStore((s) => s.shortcuts);
  const { setSidebarOpen, toggleSidebar, isLocked, setLocked } = useUIStore();
  const openShift = useShiftStore((s) => s.openShift);
  const isMobilePos = useIsMobilePos();
  const [mobileScanSearch, setMobileScanSearch] = useState('');

  /** Focus the visible scan field (desktop vs mobile each have their own input). */
  const focusBarcodeField = useCallback((opts?: { select?: boolean }) => {
    const el = isMobilePos ? mobileBarcodeInputRef.current : desktopBarcodeInputRef.current;
    if (!el || el.disabled) return;
    // Defer so focus wins after modals unmount / disabled→enabled re-render
    requestAnimationFrame(() => {
      const target = isMobilePos ? mobileBarcodeInputRef.current : desktopBarcodeInputRef.current;
      if (!target || target.disabled) return;
      target.focus({ preventScroll: true });
      if (opts?.select) target.select();
    });
  }, [isMobilePos]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [setSidebarOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileCategoryDropdownRef.current && !mobileCategoryDropdownRef.current.contains(event.target as Node)) {
        setShowMobileCategories(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as PosSettings;
    },
  });

  const { data: promoCampaigns = [] } = useQuery({
    queryKey: ['posPromotions'],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const row = await companyDb().catalog_settings.get('promotions');
        return (row?.value || []) as PromotionCampaign[];
      }
      try {
        const res = await promotionsApi.getActive();
        return (res.data.message || []) as PromotionCampaign[];
      } catch {
        const row = await companyDb().catalog_settings.get('promotions');
        return (row?.value || []) as PromotionCampaign[];
      }
    },
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (posSettings) {
      setLocaleContext({
        country: posSettings.country || 'GB',
        currency: posSettings.currency || 'GBP',
      });
      if (!posSettings.enable_delivery) {
        if (cart.fulfillmentType === 'Delivery') {
          cart.setFulfillmentType('Pickup');
        }
        if (cart.deliveryInfo?.delivery_charge) {
          cart.setDeliveryInfo({ delivery_charge: 0 });
        }
      }
    }
  }, [posSettings, cart.fulfillmentType, cart.deliveryInfo?.delivery_charge]);

  // Best-effort printer reconnect when Auto-connect is enabled
  useEffect(() => {
    void tryAutoConnectPrinter();
  }, []);

  const { data: packageLimits } = useQuery({
    queryKey: ['packageLimits'],
    queryFn: async () => {
      const res = await posApi.getPackageLimits();
      return res.data.message as PackageLimits;
    },
  });

  const invoiceLimits = packageLimits?.invoices;

  const allowOos = !!posSettings?.allow_sell_out_of_stock_item;
  const maxDiscountPct = getEffectiveDiscountCap(permissions, posSettings?.max_discount ?? 0);
  const userCanEditRate = canEditCartRate(role, permissions);
  const touchMode = touchModeOverride ?? !!posSettings?.pos_touch_mode;
  const canProcessReturns = permissions?.can_process_returns !== false;
  const userCanAddExpenses = canAddExpenses(role, permissions);

  const { data: hotSellingRows = [], isLoading: isHotSellingLoading, isError: isHotSellingError } = useQuery({
    queryKey: ['hotSellingItems', viewMode],
    queryFn: async () => {
      const res = await posApi.getHotSellingItems({
        limit: 20,
        days: 7,
        item_type: viewMode === 'products' ? 'Product' : 'Service',
      });
      return (res.data.message || []) as POSItem[];
    },
    staleTime: 60_000,
    retry: 1,
  });

  useQuery({
    queryKey: ['userFavorites', userId],
    queryFn: async () => {
      const res = await posApi.getUserFavorites();
      const ids = res.data.message as string[];
      if (userId) setFavoritesRemote(userId, ids);
      return ids;
    },
    enabled: !!userId,
  });

  useCustomerDisplayBridge(profile ?? undefined);

  const allCatalogItems = useQuery({
    queryKey: ['posCatalogForQuickAccess'],
    queryFn: async () => {
      const res = await posApi.getItems({ limit: 500, item_type: 'Product' });
      const raw = (res.data.message?.items || res.data.message || []) as POSItem[];
      return (Array.isArray(raw) ? raw : []).filter((i) => !isGeneralItemProduct(i));
    },
    staleTime: 120_000,
  }).data || [];

  const subtotal = cart.getSubtotal();
  const promoEval = useMemo(
    () => evaluateCart(cart.items, cart.customerType || 'Retailer', promoCampaigns),
    [cart.items, cart.customerType, promoCampaigns],
  );
  const billedSubtotal = promoEval.total_saving > 0 ? promoEval.remaining_subtotal : subtotal;
  const promoBanners = useMemo(
    () => activeBanners(promoCampaigns, cart.customerType || 'Retailer'),
    [promoCampaigns, cart.customerType],
  );

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);

  // Fetch items with filters
  const {
    data: itemsData,
    isLoading,
    isError: itemsError,
    error: itemsErr,
    refetch: refetchItems,
  } = useQuery({
    queryKey: ['posItems', search, activeCategory, viewMode, posSyncMode, catalogSyncedAt],
    queryFn: async () => {
      const item_type = viewMode === 'products' ? 'Product' : 'Service';
      if (isLocalFirstMode()) {
        let items = await getItemsForPos({ search: search || undefined, item_type, limit: 100 });
        if (activeCategory !== 'All' && activeCategory !== RECENT_CATEGORY) {
          items = items.filter((i) => i.category === activeCategory || (i as { category_name?: string }).category_name === activeCategory);
        }
        return { items, total: items.length };
      }
      const res = await posApi.getItems({
        search: search || undefined,
        category: activeCategory !== 'All' && activeCategory !== RECENT_CATEGORY ? activeCategory : undefined,
        item_type,
        limit: 100,
      });
      return res.data.message;
    },
  });

  const { data: posCustomFields = [] } = useQuery({
    queryKey: ['customFields', 'pos'],
    queryFn: async () => {
      const res = await posApi.getCustomFields();
      return (res.data.message as POSCustomField[]).filter((field) => !!field.show_in_pos);
    },
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['posCategories', viewMode, posSyncMode, catalogSyncedAt],
    queryFn: async () => {
      const item_type = viewMode === 'products' ? 'Product' : 'Service';
      if (isLocalFirstMode()) {
        const { companyDb } = await import('@/offline');
        const row = await companyDb().catalog_settings.get('categories');
        const cats = (row?.value as POSCategory[]) || [];
        return cats.filter((c) => !c.category_type || c.category_type === item_type);
      }
      const res = await posApi.getCategories({ category_type: item_type });
      return res.data.message || [];
    },
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['posCustomers', posSyncMode, catalogSyncedAt],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        return getCustomersForPos({ limit: 50 });
      }
      const res = await posApi.getCustomers({ limit: 50 });
      return res.data.message?.customers || res.data.message || [];
    },
  });

  const { data: pendingInvoices = [] } = useQuery({
    queryKey: ['pendingInvoices'],
    queryFn: async () => {
      const res = await posApi.getPendingInvoices();
      return res.data.message || [];
    },
    refetchInterval: 60_000,
  });

  const items: POSItem[] = useMemo(() => {
    const raw = Array.isArray(itemsData)
      ? (itemsData as POSItem[])
      : ((itemsData as { items?: POSItem[] } | undefined)?.items || []);
    return raw.filter((i) => !isGeneralItemProduct(i));
  }, [itemsData]);

  const favoriteProducts = useMemo(() => {
    const catalog = new Map<string, POSItem>();
    for (const item of [...allCatalogItems, ...items]) {
      catalog.set(item.name, item);
    }
    return favoriteIds.map((id) => catalog.get(id)).filter(Boolean) as POSItem[];
  }, [favoriteIds, allCatalogItems, items]);

  const hotSellingProducts = useMemo(() => {
    if (!hotSellingRows.length) return [];
    const isFullItem = (row: POSItem) => Boolean(row.item_name && (row.sale_price != null || row.item_type));
    if (isFullItem(hotSellingRows[0])) {
      return hotSellingRows.filter((row) => isFullItem(row));
    }
    return hotSellingRows
      .map((r) =>
        allCatalogItems.find((i) => i.name === r.name)
        || items.find((i) => i.name === r.name),
      )
      .filter(Boolean) as POSItem[];
  }, [hotSellingRows, allCatalogItems, items]);

  const gridItems = useMemo(() => {
    if (activeCategory !== RECENT_CATEGORY) return items.filter((i) => !isGeneralItemProduct(i));
    let recent = hotSellingProducts;
    const q = search.trim().toLowerCase();
    if (q) {
      recent = recent.filter(
        (i) =>
          i.item_name.toLowerCase().includes(q)
          || (i.item_code && i.item_code.toLowerCase().includes(q))
          || (i.barcode && i.barcode.toLowerCase().includes(q)),
      );
    }
    return recent.filter((i) => !isGeneralItemProduct(i));
  }, [activeCategory, items, hotSellingProducts, search]);

  const categories = useMemo(() => {
    const list = [...(categoriesData || [])] as POSCategory[];
    list.sort((a, b) => {
      const orderA = a.display_order ?? 0;
      const orderB = b.display_order ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.category_name || '').localeCompare(b.category_name || '');
    });
    return list;
  }, [categoriesData]);

  const mobileActiveCategoryLabel = useMemo(() => {
    if (activeCategory === RECENT_CATEGORY) return t('pos.hotSelling');
    if (activeCategory === 'All') return t('common.all');
    const cat = categories.find((c) => c.name === activeCategory);
    return cat?.category_name || t('common.category');
  }, [activeCategory, categories, t]);

  const customers: BackendCustomer[] = customersData ?? EMPTY_POS_CUSTOMERS;

  // Default to Zustand customer, custom default POS customer, or Walk in customer
  useEffect(() => {
    if (!hasInitializedCustomer.current && customers.length > 0) {
      if (cart.customer) {
        const c = customers.find((cust) => cust.name === cart.customer);
        setSelectedCustomer({
          id: cart.customer,
          name: cart.customerName,
          phone: c?.mobile || '',
          customer_type: c?.customer_type,
          is_default: c?.is_default,
          address: c?.address || '',
          postal_code: c?.postal_code || '',
          city: c?.city || '',
          is_walk_in: c?.is_walk_in,
          custom_prices: c?.custom_prices || cart.customerCustomPrices || [],
        });
      } else {
        let defaultCustomer = customers.find((c) => c.is_default);
        if (!defaultCustomer) {
          defaultCustomer = customers.find((c) => c.is_walk_in);
        }
        if (defaultCustomer) {
          setSelectedCustomer({
            id: defaultCustomer.name,
            name: defaultCustomer.customer_name,
            phone: defaultCustomer.mobile || '',
            customer_type: defaultCustomer.customer_type,
            is_default: defaultCustomer.is_default,
            address: defaultCustomer.address || '',
            postal_code: defaultCustomer.postal_code || '',
            city: defaultCustomer.city || '',
            is_walk_in: defaultCustomer.is_walk_in,
            custom_prices: defaultCustomer.custom_prices || [],
          });
        }
      }
      hasInitializedCustomer.current = true;
    }
  }, [customers, cart.customer, cart.customerName]);

  // Sync selected customer state with the global cart store for dynamic rates and customer types
  useEffect(() => {
    if (!hasInitializedCustomer.current) return;

    const setCustomer = useCartStore.getState().setCustomer;
    if (selectedCustomer.id) {
      const match = customers.find((c) => c.name === selectedCustomer.id);
      const cp = selectedCustomer.custom_prices || match?.custom_prices || [];
      setCustomer(
        selectedCustomer.id,
        selectedCustomer.name,
        selectedCustomer.customer_type || 'Retailer',
        cp,
        posSettings?.delivery_pricing_mode
      );
    } else {
      setCustomer('', selectedCustomer.name || WALK_IN_CUSTOMER_ID, 'Retailer', [], posSettings?.delivery_pricing_mode);
    }
  }, [selectedCustomer.id, selectedCustomer.name, selectedCustomer.custom_prices, customers, posSettings?.delivery_pricing_mode]);

  const handleAddItem = useCallback(async (
    item: POSItem,
    variant?: ItemVariant,
    qtyOverride?: number,
    serialInfo?: { serial?: string; imei?: string; mac_address?: string },
    scanMeta?: {
      weight_source?: string;
      scanned_scale_barcode?: string;
      scale_plu?: string;
      scale_weight?: number;
    },
    ageVerified = false,
    verifiedYearOverride?: number,
  ) => {
    setSaleError('');
    if (item.has_variants && !variant) {
      if ((item.variants?.length ?? 0) > 0) {
        setVariantModalItemName(item.name);
        return;
      }
      setSaleError('No variants available for this item');
      return;
    }

    // Age restriction check
    const minAge = Number(item.minimum_age) || 0;
    const currentYear = new Date().getFullYear();
    const effectiveBirthYear = verifiedYearOverride ?? verifiedCustomerBirthYear;
    const isCustomerAlreadyVerified = effectiveBirthYear !== null && (currentYear - effectiveBirthYear) >= minAge;
    const alreadyInCart = cart.items.some((c) => c.item === item.name);
    if (
      !isQuotationMode &&
      item.item_type === 'Product' &&
      Boolean(item.has_age_restriction) &&
      minAge > 0 &&
      !ageVerified &&
      !isCustomerAlreadyVerified &&
      !alreadyInCart
    ) {
      setAgePending({ item, variant, qtyOverride, serialInfo, scanMeta });
      return;
    }

    const isWeighted = !!(item.is_weighted);
    const allowFrac = !!(item.allow_fractional_qty || isWeighted);
    const tracksSerial = !!(item.track_serials) && !isQuotationMode;

    if (tracksSerial) {
      if (isLocalFirstMode()) {
        setSaleError(t('pos.serialOnlineOnly'));
        return;
      }
      if (!serialInfo?.imei && !serialInfo?.serial) {
        setSerialPending({ item, variant });
        return;
      }
      const already = cart.items.some(
        (c) => c.imei && serialInfo.imei && c.imei === serialInfo.imei
      );
      if (already) {
        setSaleError(t('pos.imeiAlreadyInCart'));
        return;
      }
    }

    // Weighted items without embedded qty → open weight modal
    if (isWeighted && (qtyOverride == null || qtyOverride <= 0)) {
      const customerType = useCartStore.getState().customerType || 'Retailer';
      const fulfillmentType = useCartStore.getState().fulfillmentType || 'Pickup';
      const customerCustomPrices = useCartStore.getState().customerCustomPrices || [];
      const variantName = variant?.name ? String(variant.name).trim() : '';
      const cp = customerCustomPrices.find((p) => {
        const itemMatches =
          p.item === item.name ||
          (item.item_code && p.item === item.item_code) ||
          (p.item_name && item.item_name && p.item_name.trim().toLowerCase() === item.item_name.trim().toLowerCase());
        if (!itemMatches) return false;
        const ruleVar = p.variant ? String(p.variant).trim() : '';
        return !ruleVar || ruleVar === variantName;
      });
      const retailRate = variant ? (variant.price || 0) : (item.sale_price || 0);
      const dp = variant ? variant.delivery_price : item.delivery_price;
      let rate = retailRate;
      if (cp) {
        if (fulfillmentType === 'Delivery' && posSettings?.delivery_pricing_mode === 'product_price') {
          rate = (cp.custom_delivery_price != null && cp.custom_delivery_price > 0)
            ? cp.custom_delivery_price
            : (dp != null && dp > 0 ? dp : cp.custom_price);
        } else {
          rate = cp.custom_price;
        }
      } else if (fulfillmentType === 'Delivery' && dp != null && dp > 0 && posSettings?.delivery_pricing_mode !== 'runtime_charge') {
        rate = dp;
      } else if (customerType === 'Wholesale') {
        const wp = variant ? variant.wholesale_price : item.wholesale_price;
        const rp = variant ? variant.reseller_price : item.reseller_price;
        rate = (wp && wp > 0) ? wp : (rp && rp > 0) ? rp : retailRate;
      } else if (customerType === 'Distributor') {
        const rp = variant ? variant.reseller_price : item.reseller_price;
        rate = (rp && rp > 0) ? rp : retailRate;
      }
      setWeightPending({ item, variant, rate });
      return;
    }

    const qty = tracksSerial ? 1 : (qtyOverride != null && qtyOverride > 0 ? qtyOverride : 1);
    if (!allowFrac && !Number.isInteger(qty)) {
      setSaleError(t('pos.fractionalNotAllowed'));
      return;
    }

    if (item.item_type === 'Product' && !canAddProductQty(item, cart.items, allowOos, qty, undefined, variant)) {
      const stock = variant ? (variant.stock_quantity ?? 0) : (item.stock_quantity ?? 0);
      const label = variant ? `${item.item_name} — ${variant.variant_name}` : item.item_name;
      if (stock <= 0) {
        setSaleError(`${label} is out of stock`);
      } else {
        const inCart = getCartQtyForItem(cart.items, item.name, undefined, variant?.name);
        setSaleError(`Only ${stock - inCart} more available for ${label}`);
      }
      return;
    }
    const customerType = useCartStore.getState().customerType || 'Retailer';
    const fulfillmentType = useCartStore.getState().fulfillmentType || 'Pickup';
    const retailRate = variant ? (variant.price || 0) : (item.sale_price || 0);
    const dp = variant ? variant.delivery_price : item.delivery_price;

    const getWholesaleRate = () => {
      const wp = variant ? variant.wholesale_price : item.wholesale_price;
      const rp = variant ? variant.reseller_price : item.reseller_price;
      if (wp != null && wp > 0) return wp;
      if (rp != null && rp > 0) return rp;
      return retailRate;
    };

    const getResellerRate = () => {
      const rp = variant ? variant.reseller_price : item.reseller_price;
      if (rp != null && rp > 0) return rp;
      return retailRate;
    };

    const wholesaleRate = getWholesaleRate();
    const resellerRate = getResellerRate();

    const customerCustomPrices = useCartStore.getState().customerCustomPrices || [];
    const variantName = variant?.name ? String(variant.name).trim() : '';
    const cp = customerCustomPrices.find((p) => {
      const itemMatches =
        p.item === item.name ||
        (item.item_code && p.item === item.item_code) ||
        (p.item_name && item.item_name && p.item_name.trim().toLowerCase() === item.item_name.trim().toLowerCase());
      if (!itemMatches) return false;
      const ruleVar = p.variant ? String(p.variant).trim() : '';
      return !ruleVar || ruleVar === variantName;
    });

    let rate = retailRate;
    if (cp) {
      if (fulfillmentType === 'Delivery' && posSettings?.delivery_pricing_mode === 'product_price') {
        rate = (cp.custom_delivery_price != null && cp.custom_delivery_price > 0)
          ? cp.custom_delivery_price
          : (dp != null && dp > 0 ? dp : cp.custom_price);
      } else {
        rate = cp.custom_price;
      }
    } else if (fulfillmentType === 'Delivery' && dp != null && dp > 0 && posSettings?.delivery_pricing_mode !== 'runtime_charge') {
      rate = dp;
    } else if (customerType === 'Wholesale') {
      rate = wholesaleRate;
    } else if (customerType === 'Distributor') {
      rate = resellerRate;
    }

    let minRate = 0;
    if (!cp && customerType === 'Retailer') {
      try {
        minRate = await getItemMinRateRouted(item.name, variant?.name);
      } catch {
        minRate = Number(variant?.retail_minimum_price || item.retail_minimum_price || variant?.minimum_price || item.minimum_price || variant?.cost_price || item.cost_price) || 0;
      }
    }
    if (!cp && minRate > 0 && rate < minRate) {
      const label = variant ? `${item.item_name} — ${variant.variant_name}` : item.item_name;
      setSaleError(`Rate for ${label} cannot be less than minimum allowed price (${formatMoney(minRate)})`);
      return;
    }
    addItemAction({
      item: item.name,
      item_name: item.item_name,
      item_code: item.item_code,
      item_type: item.item_type,
      category: item.category || '',
      quantity: qty,
      rate,
      amount: calcLineAmount(qty, rate),
      variant: variant?.name,
      variant_name: variant?.variant_name || '',
      unit_of_measurement: variant?.unit_of_measurement || item.unit_of_measurement || (item.item_type === 'Product' ? 'pcs' : 'job'),
      stock_quantity: item.item_type === 'Product'
        ? (variant ? (variant.stock_quantity ?? 0) : (item.stock_quantity ?? 0))
        : undefined,
      min_rate: minRate,
      wholesale_price: wholesaleRate,
      delivery_price: dp || 0,
      reseller_price: resellerRate,
      minimum_price: variant ? variant.minimum_price : item.minimum_price,
      retail_price: retailRate,
      customer_custom_price: cp?.custom_price,
      tax_rate: Number(item.tax_rate) || 0,
      is_weighted: isWeighted,
      allow_fractional_qty: allowFrac,
      has_age_restriction: Boolean(item.has_age_restriction),
      minimum_age: item.minimum_age,
      verified_birth_year: effectiveBirthYear ?? undefined,
      track_batches: !!item.track_batches,
      track_serials: tracksSerial,
      serial: serialInfo?.serial,
      imei: serialInfo?.imei,
      mac_address: serialInfo?.mac_address,
      service_estimated_time: item.service_estimated_time ?? null,
      service_time_unit: item.service_time_unit ?? null,
      service_staff_required: !!item.service_staff_required,
      service_appointment: item.service_appointment || 'No Appointment',
      ...(scanMeta?.weight_source
        ? {
            weight_source: scanMeta.weight_source,
            scanned_scale_barcode: scanMeta.scanned_scale_barcode,
            scale_plu: scanMeta.scale_plu,
            scale_weight: scanMeta.scale_weight,
          }
        : {}),
    });
    playBellSound();
  }, [addItemAction, allowOos, cart.items, t, verifiedCustomerBirthYear, isQuotationMode]);

  const handleAgeConfirm = useCallback((birthYear: number) => {
    if (agePending) {
      const pending = agePending;
      setVerifiedCustomerBirthYear(birthYear);
      setAgePending(null);
      toast.success(`Age verified (${birthYear}) for ${pending.item.item_name}`);
      handleAddItem(pending.item, pending.variant, pending.qtyOverride, pending.serialInfo, pending.scanMeta, true, birthYear);
    }
  }, [agePending, handleAddItem]);

  const handleAgeCancel = useCallback(() => {
    if (agePending) {
      toast.error(`Sale cancelled: ${agePending.item.item_name} requires customer age verification`);
    }
    setAgePending(null);
  }, [agePending]);

  const addScannedToCart = useCallback((result: BarcodeScanResult) => {
    const variant: ItemVariant | undefined = result.variant
      ? {
        name: result.variant,
        variant_name: result.variant_name || '',
        price: result.rate,
        unit_of_measurement: result.unit_of_measurement || 'pcs',
        parent_item: result.item,
        stock_quantity: result.stock_quantity,
      }
      : undefined;

    const isWeighted = !!(result.is_weighted);
    const scannedQty = result.quantity != null && result.quantity > 0 ? result.quantity : undefined;

    const posItem: POSItem = {
      name: result.item,
      item_code: '',
      item_name: result.item_name,
      category: '',
      item_type: result.item_type,
      sale_price: result.rate,
      has_variants: !!result.variant,
      is_active: true,
      stock_quantity: result.stock_quantity,
      unit_of_measurement: result.unit_of_measurement,
      tax_rate: Number(result.tax_rate) || 0,
      wholesale_price: result.wholesale_price,
      reseller_price: result.reseller_price,
      minimum_price: result.minimum_price,
      retail_minimum_price: result.retail_minimum_price,
      is_weighted: isWeighted ? 1 : 0,
      allow_fractional_qty: result.allow_fractional_qty || isWeighted ? 1 : 0,
      track_batches: result.track_batches ? 1 : 0,
      track_serials: result.track_serials ? 1 : 0,
      has_age_restriction: result.has_age_restriction,
      minimum_age: result.minimum_age,
      plu_code: result.plu_code,
    };

    if (result.item_type === 'Product' && viewMode === 'services') {
      setSaleError('This barcode is a product. Switch to Products.');
      return;
    }
    if (result.item_type === 'Service' && viewMode === 'products') {
      setSaleError('This barcode is a service. Switch to Services.');
      return;
    }

    const serialInfo = result.imei || result.serial
      ? { serial: result.serial, imei: result.imei, mac_address: result.mac_address }
      : undefined;

    const scanMeta = result.plu_parsed || result.weight_source === 'scale_barcode'
      ? {
          weight_source: result.weight_source || 'scale_barcode',
          scanned_scale_barcode: result.scanned_scale_barcode || result.barcode,
          scale_plu: result.scale_plu || result.plu_code,
          scale_weight: result.scale_weight ?? scannedQty,
        }
      : undefined;

    // PLU with embedded weight → add with that qty; otherwise weighted opens modal
    if (isWeighted && scannedQty && scannedQty !== 1) {
      handleAddItem(posItem, variant, scannedQty, serialInfo, scanMeta);
    } else {
      handleAddItem(posItem, variant, isWeighted ? undefined : scannedQty, serialInfo, scanMeta);
    }
  }, [handleAddItem, viewMode]);

  const getScanBarcodeOptions = useCallback(() => ({
    enableScaleBarcode: isScaleBarcodeEnabled(),
    profile: getActiveScaleSettings().scaleBarcodeProfile,
  }), []);

  const shopCountry = posSettings?.country || profile?.country || 'GB';

  const openUnknownBarcodeModal = useCallback((code: string) => {
    if (!isEuOrUk(shopCountry)) {
      setScanError('Barcode not found');
      return;
    }
    setScanError('');
    setUnknownBarcode(code);
    setBarcodeScan('');
    setMobileScanSearch('');
  }, [shopCountry]);

  const handleUnknownBarcodeSave = useCallback(async (payload: UnknownBarcodeSavePayload) => {
    if (payload.persist) {
      const res = await posApi.createItemFromBarcodeScan({
        barcode: payload.barcode,
        item_name: payload.item_name,
        sale_price: payload.sale_price,
        brand: payload.brand || undefined,
        size: payload.size || undefined,
        category: payload.category || undefined,
        tax_rate: payload.tax_rate,
        description: payload.description,
        image_url: payload.image_url,
        lookup_source: payload.lookup_source,
        manager_approver: payload.manager_approver,
      });
      const created = res.data.message as BarcodeScanResult & { item: string; rate: number; image?: string };
      addScannedToCart({
        item: created.item,
        item_code: created.item_code,
        item_name: created.item_name,
        item_type: created.item_type || 'Product',
        rate: created.rate,
        barcode: created.barcode || payload.barcode,
        tax_rate: created.tax_rate,
        stock_quantity: created.stock_quantity,
        unit_of_measurement: created.unit_of_measurement,
        wholesale_price: created.wholesale_price,
        delivery_price: created.delivery_price,
        reseller_price: created.reseller_price,
        minimum_price: created.minimum_price,
        retail_minimum_price: created.retail_minimum_price,
        has_age_restriction: created.has_age_restriction,
        minimum_age: created.minimum_age,
        is_weighted: created.is_weighted,
        variant: null,
      });
      toast.success('Product saved and added');
      qc.invalidateQueries({ queryKey: ['posItems'] });
    } else {
      const lineName = payload.size
        ? `${payload.item_name} (${payload.size})`
        : payload.item_name;
      cart.addItem({
        item: 'General Item',
        item_name: lineName,
        item_type: 'Product',
        quantity: 1,
        rate: payload.sale_price,
        amount: payload.sale_price,
        tax_rate: payload.tax_rate || 0,
        line_meta: payload.barcode ? `Barcode ${payload.barcode}` : undefined,
      } as CartItem);
      playBellSound();
      toast.success('Added to invoice only');
    }
    setUnknownBarcode(null);
    setBarcodeScan('');
    setMobileScanSearch('');
    focusBarcodeField();
  }, [addScannedToCart, cart, focusBarcodeField, qc]);

  const handleBarcodeSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    const code = barcodeScan.trim();
    if (!code || scanPending) return;

    setScanPending(true);
    setScanError('');
    setSaleError('');
    try {
      const res = await posApi.scanBarcode(code, getScanBarcodeOptions());
      const result = res.data.message as BarcodeScanResult;
      addScannedToCart(result);
      setBarcodeScan('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const text = typeof msg === 'string' ? msg : 'Barcode not found';
      if (/not found/i.test(text)) {
        openUnknownBarcodeModal(code);
      } else {
        setScanError(text);
      }
    } finally {
      setScanPending(false);
      if (!unknownBarcode) focusBarcodeField();
    }
  }, [addScannedToCart, barcodeScan, focusBarcodeField, getScanBarcodeOptions, openUnknownBarcodeModal, scanPending, unknownBarcode]);

  const handleMobileScanSearchSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    const code = mobileScanSearch.trim();
    if (!code || scanPending) return;

    setScanPending(true);
    setScanError('');
    setSaleError('');
    try {
      const res = await posApi.scanBarcode(code, getScanBarcodeOptions());
      const result = res.data.message as BarcodeScanResult;
      addScannedToCart(result);
      setMobileScanSearch('');
      setSearch('');
    } catch {
      if (isEuOrUk(shopCountry) && code.length >= 8 && /^\d[\d\-\s]+$/.test(code)) {
        openUnknownBarcodeModal(code);
      } else {
        setSearch(code);
        setScanError('');
      }
    } finally {
      setScanPending(false);
      if (!unknownBarcode) focusBarcodeField();
    }
  }, [addScannedToCart, focusBarcodeField, getScanBarcodeOptions, mobileScanSearch, openUnknownBarcodeModal, scanPending, shopCountry, unknownBarcode]);

  useEffect(() => {
    if (isMobilePos) {
      setSearch(mobileScanSearch);
    }
  }, [isMobilePos, mobileScanSearch]);

  const handleCameraScan = useCallback(async (code: string): Promise<boolean> => {
    if (!code || scanPending) return false;
    setBarcodeScan(code);
    setScanPending(true);
    setScanError('');
    setSaleError('');
    try {
      const res = await posApi.scanBarcode(code, getScanBarcodeOptions());
      const result = res.data.message as BarcodeScanResult;
      addScannedToCart(result);
      setBarcodeScan('');
      setMobileScanSearch('');
      setSearch('');
      return true;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const text = typeof msg === 'string' ? msg : 'Barcode/QR Code not found';
      if (/not found/i.test(text)) {
        openUnknownBarcodeModal(code);
        return true;
      }
      setScanError(text);
      return false;
    } finally {
      setScanPending(false);
      if (!unknownBarcode) focusBarcodeField();
    }
  }, [addScannedToCart, focusBarcodeField, getScanBarcodeOptions, openUnknownBarcodeModal, scanPending, unknownBarcode]);

  // Keep scan field focused whenever checkout/receipt modals close (ready for next sale)
  useEffect(() => {
    if (!showPayment && !showReceipt && !scanPending && !isLocked) {
      focusBarcodeField();
    }
  }, [showPayment, showReceipt, viewMode, scanPending, isLocked, focusBarcodeField]);

  const handleUpdateQuantity = useCallback((idx: number, qty: number, promptRemoval: boolean = false) => {
    if (qty <= 0) {
      if (promptRemoval) {
        if (confirm('Remove item from cart?')) {
          cart.removeItem(idx);
        }
      }
      return;
    }
    setSaleError('');
    const line = cart.items[idx];
    if (line.item_type === 'Product' && !line.allow_fractional_qty && !line.is_weighted && !Number.isInteger(qty)) {
      setSaleError(t('pos.fractionalNotAllowed'));
      return;
    }
    if (line.item_type === 'Product' && !allowOos) {
      const stock = line.stock_quantity ?? 0;
      const otherInCart = getCartQtyForItem(cart.items, line.item, idx, line.variant);
      const label = line.variant_name ? `${line.item_name} — ${line.variant_name}` : line.item_name;
      if (stock <= 0) {
        setSaleError(`${label} is out of stock`);
        return;
      }
      if (qty + otherInCart > stock) {
        setSaleError(`Only ${stock - otherInCart} more available for ${label}`);
        return;
      }
    }
    cart.updateQuantity(idx, qty);
  }, [allowOos, cart, t]);

  useEffect(() => {
    const len = cart.items.length;
    if (len === 0) {
      setSelectedCartLineIdx(null);
    } else if (len > prevCartLenRef.current) {
      setSelectedCartLineIdx(len - 1);
    } else {
      setSelectedCartLineIdx((idx) => (idx != null && idx >= len ? len - 1 : idx));
    }
    prevCartLenRef.current = len;
  }, [cart.items.length]);

  useEffect(() => {
    if (selectedCartLineIdx == null) return;
    cartLineRefs.current[selectedCartLineIdx]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedCartLineIdx]);

  const moveCartSelection = useCallback((delta: -1 | 1) => {
    if (cart.items.length === 0) return;
    setSelectedCartLineIdx((idx) => {
      const cur = idx ?? cart.items.length - 1;
      return Math.max(0, Math.min(cart.items.length - 1, cur + delta));
    });
  }, [cart.items.length]);

  const handleUpdateRateChange = useCallback((idx: number, raw: string) => {
    const rate = raw === '' ? 0 : parseFloat(raw);
    if (Number.isNaN(rate) || rate < 0) return;
    cart.updateRate(idx, rate);
  }, [cart]);

  const handleValidateRate = useCallback(async (idx: number) => {
    const line = cart.items[idx];
    if (!line) return;
    const rate = line.rate;

    const isCustomerCustom = line.customer_custom_price != null && line.customer_custom_price > 0;
    if (isCustomerCustom) {
      setSaleError('');
      return;
    }

    const customerType = useCartStore.getState().customerType || 'Retailer';
    let minRate: number = 0;
    if (customerType === 'Retailer') {
      if (line.min_rate == null) {
        try {
          minRate = await getItemMinRateRouted(line.item, line.variant);
          const items = [...useCartStore.getState().items];
          if (items[idx]) {
            items[idx] = { ...items[idx], min_rate: minRate };
            useCartStore.setState({ items });
          }
        } catch {
          minRate = 0;
        }
      } else {
        minRate = line.min_rate;
      }
    }

    if (minRate > 0 && rate < minRate) {
      const label = line.variant_name ? `${line.item_name} — ${line.variant_name}` : line.item_name;
      if (canBypassStaffLimits(role) || managerApprover) {
        setSaleError('');
        return;
      }
      if (userCanEditRate) {
        pendingRateIdxRef.current = idx;
        pendingRateRef.current = rate;
        setOverrideMessage(`Rate for ${label} is below minimum (${formatMoney(minRate)}). Manager approval required.`);
        setShowManagerOverride(true);
        return;
      }
      setSaleError(`Rate for ${label} cannot be less than minimum allowed price (${formatMoney(minRate)})`);
      cart.updateRate(idx, minRate);
    } else {
      setSaleError('');
    }
  }, [cart, role, managerApprover, userCanEditRate]);

  const calculateAndSetDiscount = useCallback((inputVal: string, type: 'amount' | 'percentage', currentSubtotal: number) => {
    const val = parseFloat(inputVal) || 0;
    let nextAmount = type === 'percentage' ? (currentSubtotal * val) / 100 : val;
    const maxAmt = getMaxDiscountAmount(currentSubtotal, maxDiscountPct);

    if (
      !canBypassStaffLimits(role) &&
      discountExceedsCap(nextAmount, currentSubtotal, maxDiscountPct) &&
      !managerApprover
    ) {
      pendingDiscountRef.current = nextAmount;
      pendingDiscountInputRef.current = inputVal;
      setOverrideMessage(`Discount exceeds your limit of ${maxDiscountPct}% (${formatMoney(Math.round(maxAmt || 0))}). Manager approval required.`);
      setShowManagerOverride(true);
      setDiscountInput(inputVal);
      return;
    }

    // SD-UK-023: dismiss approval modal when amount is within cap
    setShowManagerOverride(false);
    pendingDiscountRef.current = 0;
    pendingDiscountInputRef.current = '';

    cart.setDiscount(nextAmount);
    setDiscountInput(inputVal);

    if (maxAmt !== null && nextAmount > maxAmt + 0.01 && !canBypassStaffLimits(role)) {
      setSaleError('');
    } else if (maxAmt !== null && nextAmount <= maxAmt + 0.01) {
      setSaleError(prev => prev.startsWith('Discount capped') || prev.includes('Manager approval') ? '' : prev);
    } else {
      setSaleError(prev => prev.startsWith('Discount capped') ? '' : prev);
    }
  }, [maxDiscountPct, cart, role, managerApprover]);

  useEffect(() => {
    // SD-UK-007/052: do not re-apply stale discountInput when cart empty or input cleared.
    // Amount mode keeps a fixed £ — only recompute when user typed %.
    const raw = discountInput.toString().trim();
    if (!cart.items.length) {
      if (showManagerOverride) setShowManagerOverride(false);
      return;
    }
    // Empty input: do not wipe cart.discount (quote convert / hold may set discount before hydrate)
    if (raw === '') return;
    if (discountType === 'amount') return;
    calculateAndSetDiscount(raw, discountType, billedSubtotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billedSubtotal, discountType, cart.items.length]);

  const calculateAndSetTax = useCallback((inputVal: string, type: 'amount' | 'percentage', currentSubtotal: number, currentDiscount: number, inclusive = false) => {
    const val = parseFloat(inputVal) || 0;
    const baseAmount = currentSubtotal - currentDiscount;
    let nextAmount: number;
    if (type === 'percentage') {
      // Inclusive: extract tax from gross; exclusive: add tax on net
      nextAmount = inclusive
        ? (baseAmount * val) / (100 + val)
        : (baseAmount * val) / 100;
    } else {
      nextAmount = val;
    }
    cart.setTax(nextAmount);
    setTaxInput(inputVal);
  }, [cart]);

  const enableItemTax = !!posSettings?.enable_item_tax;
  const pricesIncludeTax = !!posSettings?.prices_include_tax;
  const taxFieldLabel = (posSettings?.tax_label || 'Tax').trim() || 'Tax';

  // Auto-sum line taxes from item tax_rate % on post-promo, post-discount amount (SD-UK-009/012).
  useEffect(() => {
    if (!enableItemTax) return;
    const hasLineTax = cart.items.some((line) => Number(line.tax_rate) > 0);
    // SD-UK-040: if convert loaded cart.tax without tax_rate on lines, do not wipe it to 0
    if (!hasLineTax) return;
    const lineBases = cart.items.map((line, idx) => {
      const amount = Number(promoEval.lines[idx]?.charged_amount ?? line.amount ?? 0);
      return Math.max(0, amount);
    });
    const baseSum = lineBases.reduce((s, a) => s + a, 0);
    const disc = Math.max(0, Number(cart.discount) || 0);
    const raw = cart.items.reduce((sum, line, idx) => {
      const rate = Number(line.tax_rate) || 0;
      if (rate <= 0) return sum;
      let amount = lineBases[idx];
      // Allocate cart discount proportionally across taxable lines
      if (disc > 0 && baseSum > 0) {
        amount = Math.max(0, amount - (disc * amount) / baseSum);
      }
      if (pricesIncludeTax) {
        return sum + (amount * rate) / (100 + rate);
      }
      return sum + amount * (rate / 100);
    }, 0);
    const next = roundMoney(raw);
    if (Math.abs(next - cart.tax) > 0.005) {
      cart.setTax(next);
      setTaxInput(String(next));
      setTaxType('amount');
    }
  }, [enableItemTax, pricesIncludeTax, cart.items, cart.tax, cart.discount, cart, promoEval.lines]);

  useEffect(() => {
    if (enableItemTax) return;
    // SD-UK-040: empty taxInput must not wipe quote/cart tax already set until user edits.
    const raw = taxInput.toString().trim();
    if (raw === '') return;
    if (taxType === 'amount') return;
    calculateAndSetTax(raw, taxType, billedSubtotal, cart.discount, pricesIncludeTax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billedSubtotal, cart.discount, taxType, enableItemTax, pricesIncludeTax]);

  // SD-UK-040: hydrate local tax/discount inputs when cart was loaded from quote convert
  useEffect(() => {
    if (!cart.items.length) return;
    if (taxInput.toString().trim() === '' && Number(cart.tax) > 0) {
      setTaxInput(String(cart.tax));
      setTaxType('amount');
    }
    if (discountInput.toString().trim() === '' && Number(cart.discount) > 0) {
      setDiscountInput(String(cart.discount));
      setDiscountType('amount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.items.length, cart.tax, cart.discount]);

  const createInvoiceMutation = useMutation({
    networkMode: 'always',
    mutationFn: async (data: CompleteSaleInput) => completeSale(data),
    onSuccess: (res, variables) => {
      const invoiceName = res.name;
      setLastInvoice(invoiceName);
      setShowPayment(false);
      setSaleError('');

      const hadCashPayment = (variables.payments || []).some(
        (p: any) => String(p.payment_method || '').toLowerCase() === 'cash' && Number(p.amount) > 0,
      );
      if (hadCashPayment && posSettings?.open_drawer_on_cash_payment !== false) {
        void (async () => {
          await printerPrimeRef.current;
          await openCashDrawer();
        })();
      }
      broadcastThankYou('Thank you for your purchase!');

      const printerSettings = usePrinterStore.getState().settings;
      const showLocalReceipt = Boolean(res.offline || isLocalFirstMode());
      const wantModal = !posSettings || posSettings.print_receipt_after_sale !== false;

      const finishUi = (preview: Record<string, unknown> | null) => {
        setReceiptPreview(preview);
        if (wantModal) {
          setShowReceipt(true);
        } else {
          // No receipt modal — return focus so the next barcode scan is ready
          focusBarcodeField();
        }
      };

      const runSilentPrint = async (inv: ReceiptData | null | undefined) => {
        if (!printerSettings.autoPrintOnSale || !inv) return;
        // Wait for connection started under the Complete Sale click
        await printerPrimeRef.current;
        const result = await silentPrintReceipt(
          inv,
          undefined,
          inv.company_name || company?.name || profile?.company_name,
          posSettings,
        );
        if (result.ok) {
          toast.success('Receipt sent to printer');
        } else {
          toast.error(result.error);
        }
      };

      if (showLocalReceipt) {
        void getLocalReceiptData(invoiceName)
          .then(async (local) => {
            await runSilentPrint(local as ReceiptData | null);
            finishUi(local);
          })
          .catch(() => {
            finishUi(null);
          });
      } else {
        void getInvoiceDetailsRouted(invoiceName)
          .then(async (details) => {
            await runSilentPrint(details as ReceiptData);
            finishUi(null);
          })
          .catch(() => {
            finishUi(null);
          });
      }

      // SD-UK-007/052: clear discount UI before cart wipe so billedSubtotal effect cannot re-apply stale input
      clearDiscountTaxInputs();
      cart.clearCart();
      setVerifiedCustomerBirthYear(null);
      let defaultCustomer = customers.find((c) => c.is_default);
      if (!defaultCustomer) {
        defaultCustomer = customers.find((c) => c.is_walk_in);
      }
      if (defaultCustomer) {
        setSelectedCustomer({
          id: defaultCustomer.name,
          name: defaultCustomer.customer_name,
          phone: defaultCustomer.mobile || '',
          customer_type: defaultCustomer.customer_type,
          is_default: defaultCustomer.is_default,
          custom_prices: defaultCustomer.custom_prices || [],
        });
        hasInitializedCustomer.current = true;
      } else {
        hasInitializedCustomer.current = false;
        setSelectedCustomer({ id: '', name: '', phone: '' });
      }
      // Don't refetch server-only queries while offline — they hang and confuse UX.
      if (navigator.onLine && useSyncStore.getState().isServerReachable) {
        qc.invalidateQueries({ queryKey: ['pendingInvoices'] });
        qc.invalidateQueries({ queryKey: ['packageLimits'] });
      }
      qc.invalidateQueries({ queryKey: ['dailySummary'] });
      qc.invalidateQueries({ queryKey: ['recentInvoices'] });
      qc.invalidateQueries({ queryKey: ['hotSellingItems'] });
      qc.invalidateQueries({ queryKey: ['posItems'] });
      qc.invalidateQueries({ queryKey: ['posCustomers'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customerInvoices'] });
      qc.invalidateQueries({ queryKey: ['salesHubList'] });
      qc.invalidateQueries({ queryKey: ['salesHubCounts'] });
    },
    onError: (err: any) => {
      let msg = err.response?.data?.message || err.message || 'Failed to create invoice';
      try {
        if (err.response?.data?._server_messages) {
          const parsed = JSON.parse(err.response.data._server_messages);
          if (parsed && parsed.length > 0) {
            const inner = JSON.parse(parsed[0]);
            if (inner && inner.message) {
              msg = inner.message;
            }
          }
        }
      } catch (e) {
        // ignore parsing errors
      }
      setSaleError(msg);
      // Keep checkout open so cashier can retry / see the error
    },
  });

  const handleCheckout = () => {
    if (invoiceLimits?.can_add_more === false) {
      setSaleError('Monthly invoice limit reached for your package. Please upgrade your plan.');
      return;
    }
    if (!selectedCustomer.id && !selectedCustomer.name.trim()) {
      setSaleError('Please select or enter a customer before checking out.');
      return;
    }

    const zeroAmountGeneralItem = cart.items.find((line) => line.item === 'General Item' && (line.amount || 0) <= 0);
    if (zeroAmountGeneralItem) {
      setSaleError('Please set a valid price for the General Item before checking out.');
      return;
    }
    const missingVariantLine = cart.items.find((line) => {
      if (line.variant) return false;
      const catalogItem =
        allCatalogItems.find((i) => i.name === line.item)
        || items.find((i) => i.name === line.item);
      return catalogItem?.has_variants && (catalogItem.variants?.length ?? 0) > 0;
    });
    if (missingVariantLine) {
      setSaleError('Variant is required for items with variants');
      setVariantModalItemName(missingVariantLine.item);
      return;
    }
    if (openShift?.is_expired) {
      setSaleError(`Shift has exceeded the ${openShift.max_shift_hours ?? 24} hour limit. Close this shift and open a new one.`);
      return;
    }
    setSaleError('');
    if (cart.fulfillmentType === 'Delivery') {
      if (!cart.deliveryInfo?.address?.trim() || !cart.deliveryInfo?.postal_code?.trim() || !cart.deliveryInfo?.phone?.trim()) {
        setShowDeliveryModal(true);
        return;
      }
      if (posSettings?.delivery_pricing_mode === 'runtime_charge' && (cart.deliveryInfo?.delivery_charge === undefined || cart.deliveryInfo?.delivery_charge === null)) {
        setShowDeliveryModal(true);
        return;
      }
    }
    const hasService = cart.items.some((i) => i.item_type === 'Service');
    if (hasService) {
      setShowServiceCheckout(true);
      return;
    }
    setShowPayment(true);
  };

  const handleServiceCheckoutConfirm = (drafts: ServiceJobDraft[]) => {
    const items = [...cart.items];
    for (const d of drafts) {
      const line = items[d.cartIndex];
      if (!line || line.item_type !== 'Service') continue;
      items[d.cartIndex] = {
        ...line,
        service_assigned_to: d.assigned_to || undefined,
        service_scheduled_at: d.serve_now ? undefined : d.scheduled_at || undefined,
        service_serve_now: d.serve_now || d.appointment_mode === 'No Appointment',
        service_appointment: d.appointment_mode,
        service_staff_required: d.staff_required,
      };
    }
    useCartStore.setState({ items });
    setShowServiceCheckout(false);
    setShowPayment(true);
  };

  const handleToggleHeldCarts = useCallback(() => {
    if (cart.holds.length === 0) {
      toast.error(t('pos.noHeldCarts'));
      return;
    }
    setShowHeldCarts((v) => !v);
    if (isMobilePos) setIsCartMobileOpen(true);
  }, [cart.holds.length, isMobilePos, t]);

  const posShortcutActionsWhenIdle: PosShortcutAction[] = [
    'showShortcuts',
    'focusSearch',
    'focusBarcode',
    'toggleFavorites',
    'openPay',
    'checkout',
    'closeModal',
    'holdCart',
    'toggleHeldCarts',
    'openExpense',
    'openReturn',
    'openTodaySales',
    'openPending',
    'removeCartLine',
    'increaseCartQty',
    'decreaseCartQty',
    'prevCartLine',
    'nextCartLine',
  ];

  const cartShortcutsBlocked =
    showPayment
    || showExpenseModal
    || showReturnModal
    || showTodaySalesModal
    || showPendingModal
    || showShortcutsHelp
    || !!variantModalItemName;

  usePosKeyboardShortcuts(
    {
      showShortcuts: () => setShowShortcutsHelp(true),
      focusSearch: () => searchInputRef.current?.focus(),
      focusBarcode: () => focusBarcodeField({ select: true }),
      toggleFavorites: () => setShowFavoritesStrip(!showFavoritesStrip),
      openPay: () => {
        if (!showPayment && cart.items.length) handleCheckout();
      },
      checkout: () => {
        if (!showPayment && cart.items.length) handleCheckout();
      },
      closeModal: () => {
        setShowPayment(false);
        setShowReceipt(false);
        setShowReturnModal(false);
        setShowExpenseModal(false);
        setShowPendingModal(false);
        setShowTodaySalesModal(false);
        setShowShortcutsHelp(false);
        setVariantModalItemName(null);
      },
      holdCart: () => {
        const result = cart.holdCart({ customerMobile: selectedCustomer.phone });
        resetLocalCartUi();
        if (result.ok) toast.success('Cart held');
        else toast.error(result.error);
      },
      toggleHeldCarts: handleToggleHeldCarts,
      openExpense: () => {
        if (userCanAddExpenses) setShowExpenseModal(true);
      },
      openReturn: () => {
        if (canProcessReturns) setShowReturnModal(true);
      },
      openTodaySales: () => setShowTodaySalesModal(true),
      openPending: () => setShowPendingModal(true),
      removeCartLine: () => {
        if (cartShortcutsBlocked) return;
        const idx = selectedCartLineIdx;
        if (idx == null || idx < 0 || idx >= cart.items.length) return;
        cart.removeItem(idx);
      },
      increaseCartQty: () => {
        if (cartShortcutsBlocked) return;
        const idx = selectedCartLineIdx;
        if (idx == null) return;
        const line = cart.items[idx];
        if (!line || line.imei || line.track_serials) return;
        const step = line.is_weighted || line.allow_fractional_qty ? 0.01 : 1;
        handleUpdateQuantity(idx, line.quantity + step, false);
      },
      decreaseCartQty: () => {
        if (cartShortcutsBlocked) return;
        const idx = selectedCartLineIdx;
        if (idx == null) return;
        const line = cart.items[idx];
        if (!line || line.imei || line.track_serials) return;
        const step = line.is_weighted || line.allow_fractional_qty ? 0.01 : 1;
        handleUpdateQuantity(idx, line.quantity - step, true);
      },
      prevCartLine: () => {
        if (cartShortcutsBlocked) return;
        moveCartSelection(-1);
      },
      nextCartLine: () => {
        if (cartShortcutsBlocked) return;
        moveCartSelection(1);
      },
      completeSale: () => {
        if (showPayment) paymentModalRef.current?.submitSale();
      },
      confirmPayment: () => {
        if (showPayment) paymentModalRef.current?.submitSale();
      },
    },
    !isLocked && !isMobilePos,
    undefined,
    showPayment ? ['showShortcuts', 'closeModal'] : posShortcutActionsWhenIdle,
  );

  const clearDiscountTaxInputs = useCallback(() => {
    setDiscountInput('');
    setDiscountType('amount');
    setTaxInput('');
    setTaxType('amount');
    cart.setDiscount(0);
    cart.setTax(0);
    // SD-UK-007/052: full sale-adjustment reset including Manager Approval
    setShowManagerOverride(false);
    setManagerApprover(null);
    pendingDiscountRef.current = null;
    pendingDiscountInputRef.current = '';
  }, [cart]);

  const resetLocalCartUi = useCallback(() => {
    clearDiscountTaxInputs();
    setSaleError('');
    let defaultCustomer = customers.find((c) => c.is_default);
    if (!defaultCustomer) {
      defaultCustomer = customers.find((c) => c.is_walk_in);
    }
    if (defaultCustomer) {
      setSelectedCustomer({
        id: defaultCustomer.name,
        name: defaultCustomer.customer_name,
        phone: defaultCustomer.mobile || '',
        customer_type: defaultCustomer.customer_type,
        is_default: defaultCustomer.is_default,
        custom_prices: defaultCustomer.custom_prices || [],
      });
      hasInitializedCustomer.current = true;
    } else {
      hasInitializedCustomer.current = false;
      setSelectedCustomer({ id: '', name: '', phone: '' });
    }
  }, [customers, clearDiscountTaxInputs]);

  /** Clear cart + discount/tax UI so next sale starts clean (SD-UK-007). */
  const handleClearCart = useCallback(() => {
    clearDiscountTaxInputs();
    cart.clearCart();
    setSaleError('');
  }, [cart, clearDiscountTaxInputs]);

  const restoreCustomerFromHold = useCallback(
    (snap: CartSnapshot) => {
      setDiscountInput(snap.discount ? String(snap.discount) : '');
      setDiscountType('amount');
      setTaxInput(snap.tax ? String(snap.tax) : '');
      setTaxType('amount');
      setSaleError('');
      if (snap.customer) {
        const matched = customers.find((c) => c.name === snap.customer);
        setSelectedCustomer({
          id: snap.customer,
          name: snap.customerName || matched?.customer_name || '',
          phone: snap.customerMobile || matched?.mobile || '',
          customer_type: snap.customerType || matched?.customer_type,
          is_default: matched?.is_default,
        });
        hasInitializedCustomer.current = true;
      } else if (snap.customerName) {
        setSelectedCustomer({
          id: '',
          name: snap.customerName,
          phone: snap.customerMobile || '',
        });
        hasInitializedCustomer.current = true;
      } else {
        resetLocalCartUi();
      }
    },
    [customers, resetLocalCartUi],
  );

  const handleHoldCart = () => {
    cart.holdCart({ customerMobile: selectedCustomer.phone || undefined });
    resetLocalCartUi();
    setShowHeldCarts(true);
    toast.success('Sale held — start a new cart');
  };

  const handleSaveQuote = async () => {
    if (!selectedCustomer.id || selectedCustomer.id === WALK_IN_CUSTOMER_ID) {
      toast.error('Please select a valid customer to save quotation');
      return;
    }
    try {
      const netTotal = cart.getSubtotal() - (cart.discount || 0) + (cart.tax || 0);
      const q = {
        client_offline_id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        customer: selectedCustomer.id,
        customerName: selectedCustomer.name,
        saas_company: company?.code || company?.name,
        items: cart.items.map(i => ({
          item: i.item,
          item_name: i.item_name,
          line_comment: i.line_comment || '',
          qty: i.quantity,
          quantity: i.quantity,
          rate: i.rate,
          amount: i.amount,
          uom: (i as any).uom || 'Nos'
        })),
        discount_amount: cart.discount || 0,
        tax_amount: cart.tax || 0,
        net_total: netTotal,
        status: 'Draft',
        valid_till: quoteExpiryDate || undefined,
        docstatus: 1
      };
      
      const { createQuotationRouted } = await import('@/offline/posModuleRouter');
      
      try {
        await createQuotationRouted(q);
        toast.success('Quotation saved successfully');
      } catch (err: any) {
        toast.error(`Failed to save quotation: ${err.message}`);
      }
      
      handleClearCart();
      setSelectedCustomer({ id: '', name: '', phone: '' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to save quotation');
    }
  };

  const handleResumeHold = (id: string) => {
    const snap = cart.holds.find((h) => h.id === id);
    const result = cart.resumeHold(id, { customerMobile: selectedCustomer.phone || undefined });
    if (!result.ok) {
      toast.error(result.error);
      setSaleError(result.error);
      return;
    }
    if (snap) {
      restoreCustomerFromHold(snap);
    }
    setShowHeldCarts(false);
    toast.success('Held cart restored');
  };

  const handleDiscardHold = (id: string) => {
    cart.discardHold(id);
    toast.success('Held cart discarded');
  };

  const deliveryCharge = (
    posSettings?.enable_delivery &&
    cart.fulfillmentType === 'Delivery' &&
    posSettings?.delivery_pricing_mode === 'runtime_charge'
  ) ? Math.max(0, Number(cart.deliveryInfo?.delivery_charge) || 0) : 0;

  const handleCompleteSale = (payments: { payment_method: string; account: string; bank_account?: string; reference_no: string; amount: number }[], writeOffAmount?: number, salesPerson?: string, invoiceDate?: string) => {
    setSaleError('');
    if (cart.fulfillmentType === 'Delivery') {
      if (!cart.deliveryInfo?.address?.trim() || !cart.deliveryInfo?.postal_code?.trim() || !cart.deliveryInfo?.phone?.trim()) {
        setSaleError('Delivery address, postal code, and contact phone are required for delivery orders');
        setShowDeliveryModal(true);
        return;
      }
      if (posSettings?.delivery_pricing_mode === 'runtime_charge' && (cart.deliveryInfo?.delivery_charge === undefined || cart.deliveryInfo?.delivery_charge === null)) {
        setSaleError('Please specify the delivery price for this order');
        setShowDeliveryModal(true);
        return;
      }
    }
    const serialMissing = cart.items.find((i) => i.track_serials && !i.imei && !i.serial);
    if (serialMissing) {
      setSaleError(t('pos.imeiRequiredForSale'));
      return;
    }
    if (isLocalFirstMode() && cart.items.some((i) => i.track_serials || i.imei)) {
      setSaleError(t('pos.serialOnlineOnly'));
      return;
    }
    const isCashier = isCashierLevel(role);
    if (isCashier && !managerApprover) {
      const customerType = useCartStore.getState().customerType || 'Retailer';
      const discountFactor = billedSubtotal > 0 ? cart.discount / billedSubtotal : 0;
      for (const item of cart.items) {
        const idx = cart.items.indexOf(item);
        const charged = promoEval.lines[idx]?.charged_amount ?? item.amount;
        const chargedRate = item.quantity ? charged / item.quantity : item.rate;
        const minRate = (customerType === 'Retailer' && !(item.customer_custom_price != null && item.customer_custom_price > 0)) ? (item.min_rate || 0) : 0;
        if (minRate > 0) {
          const effectiveRate = chargedRate * (1 - discountFactor);
          if (effectiveRate < minRate) {
            const label = item.variant_name ? `${item.item_name} — ${item.variant_name}` : item.item_name;
            setSaleError(`Discounted rate for ${label} (${formatMoney(effectiveRate)}) cannot be less than minimum allowed price (${formatMoney(minRate)})`);
            return;
          }
        }
      }
    }

    // Start BT connect under this click — before any await — so the browser picker works
    printerPrimeRef.current = primePrinterForAutoPrint();

    const selectedCustomerRecord = customers.find((c) => c.name === selectedCustomer.id);
    const saleBirthYear = verifiedCustomerBirthYear || cart.items.find((i) => i.verified_birth_year)?.verified_birth_year || null;

    createInvoiceMutation.mutate({
      customer: selectedCustomer.id || WALK_IN_CUSTOMER_ID,
      customerName: selectedCustomer.name,
      customer_birth_year: saleBirthYear,
      previous_outstanding: selectedCustomerRecord?.outstanding_balance || 0,
      fulfillment_type: cart.fulfillmentType || 'Pickup',
      delivery_status: cart.fulfillmentType === 'Delivery' ? 'Pending' : undefined,
      delivery_address: cart.deliveryInfo?.address || '',
      delivery_postal_code: cart.deliveryInfo?.postal_code || '',
      delivery_recipient_name: cart.deliveryInfo?.recipient_name || selectedCustomer.name || '',
      delivery_phone: cart.deliveryInfo?.phone || selectedCustomer.phone || '',
      delivery_notes: cart.deliveryInfo?.notes || '',
      delivery_charge: deliveryCharge,
      items: cart.items.map(i => ({
        item: i.item, item_name: i.item_name, item_type: i.item_type,
        line_comment: i.line_comment || '',
        quantity: i.quantity, rate: i.rate, amount: i.amount,
        category: i.category,
        variant: i.variant, variant_name: i.variant_name,
        retail_price: i.retail_price,
        customer_custom_price: i.customer_custom_price,
        is_customer_custom_price: Boolean(i.customer_custom_price != null && i.customer_custom_price > 0),
        batch: i.batch, batch_no: i.batch_no, expiry_date: i.expiry_date,
        is_weighted: i.is_weighted ? 1 : 0,
        serial: i.serial, imei: i.imei, mac_address: i.mac_address,
        customer_birth_year: i.verified_birth_year || saleBirthYear || null,
        ...(i.weight_source
          ? {
              weight_source: i.weight_source,
              scanned_scale_barcode: i.scanned_scale_barcode,
              scale_plu: i.scale_plu,
              scale_weight: i.scale_weight,
            }
          : {}),
        ...(i.item_type === 'Service'
          ? {
              assigned_to: i.service_assigned_to,
              scheduled_at: i.service_scheduled_at,
              serve_now: i.service_serve_now !== false && !i.service_scheduled_at,
              appointment_mode: i.service_appointment,
            }
          : {}),
      })),
      // Persist cart-computed promotions so invoice matches till (SD-UK-026)
      promo_lines: promoEval.lines.map((pl) => ({
        charged_amount: pl.charged_amount,
        promo_amount: pl.promo_amount,
        promo_qty: pl.promo_qty,
        promo_campaign: pl.promo_campaign,
        promo_campaign_name: pl.promo_campaign_name,
        promo_rule: pl.promo_rule,
        promo_rule_name: pl.promo_rule_name,
        promo_rule_type: pl.promo_rule_type,
      })),
      discount_amount: cart.discount,
      tax_amount: cart.tax,
      write_off_amount: writeOffAmount,
      prices_include_tax: pricesIncludeTax,
      payments,
      notes: cart.notes,
      manager_approver: managerApprover || undefined,
      sales_person: salesPerson || undefined,
      invoice_date: invoiceDate || undefined,
      ...(openShift ? { pos_shift: openShift.name } : {}),
    });
  };

  const netTotal = roundMoney(pricesIncludeTax
    ? Math.max(0, billedSubtotal - cart.discount) + deliveryCharge
    : Math.max(0, billedSubtotal - cart.discount + cart.tax) + deliveryCharge);
  // BACKUP: previously blocked unpaid/partial for walk in
  // const selectedCustomerRecord = customers.find((c) => c.name === selectedCustomer.id);
  // const isWalkInCustomer = Boolean(selectedCustomerRecord?.is_walk_in);
  const allowCreditSale = true;
  const variantModalItem = useMemo(() => {
    if (!variantModalItemName) return null;
    return (
      items.find((i) => i.name === variantModalItemName)
      || gridItems.find((i) => i.name === variantModalItemName)
      || allCatalogItems.find((i) => i.name === variantModalItemName)
      || null
    );
  }, [items, gridItems, variantModalItemName, allCatalogItems]);

  const renderedItemsGrid = useMemo(() => {
    if (activeCategory === RECENT_CATEGORY && isHotSellingError) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400 p-4">
          <div className="text-center max-w-sm">
            <Flame className="w-12 h-12 mx-auto mb-3 opacity-30 text-orange-400" />
            <p className="text-gray-600 font-medium">{t('pos.hotSellingLoadFailed')}</p>
            <button
              type="button"
              onClick={() => void qc.invalidateQueries({ queryKey: ['hotSellingItems'] })}
              className="mt-3 text-sm text-primary-700 font-medium hover:underline"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      );
    }
    if (activeCategory === RECENT_CATEGORY && isHotSellingLoading) {
      return <div className="flex items-center justify-center h-full text-gray-400">{t('common.loading')}</div>;
    }
    if (isLoading && activeCategory !== RECENT_CATEGORY) {
      return <div className="flex items-center justify-center h-full text-gray-400">{t('common.loading')}</div>;
    }
    if (activeCategory === RECENT_CATEGORY && gridItems.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400 p-4">
          <div className="text-center max-w-sm">
            <Flame className="w-12 h-12 mx-auto mb-3 opacity-30 text-orange-400" />
            <p className="text-gray-600 font-medium">{t('pos.noHotSelling')}</p>
            <p className="text-xs text-gray-500 mt-2">{t('pos.noHotSellingHint')}</p>
          </div>
        </div>
      );
    }
    if (itemsError || gridItems.length === 0) {
      const live = !isLocalFirstMode(posSyncMode);
      return (
        <div className="flex items-center justify-center h-full text-gray-400 p-4">
          <div className="text-center max-w-sm">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-gray-600 font-medium">
              {itemsError ? t('pos.loadFailed') : viewMode === 'products' ? t('pos.noProducts') : t('pos.noServices')}
            </p>
            {live && (
              <p className="text-xs text-amber-800 mt-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-left">
                Live Online needs a working cloud session. After PIN unlock, use the amber{' '}
                <strong>Online login</strong> banner so APIs can load products — or switch to Local
                First / Offline Always and download the catalog in Settings → POS Sync Mode.
              </p>
            )}
            {!live && !itemsError && (
              <p className="text-xs text-gray-500 mt-2">
                Local catalog is empty — Settings → POS Sync Mode → Refresh Local Catalog (while online).
              </p>
            )}
            <button
              type="button"
              onClick={() => void refetchItems()}
              className="mt-3 text-sm text-primary-700 font-medium hover:underline"
            >
              {t('common.retry')}
            </button>
            {itemsErr ? (
              <p className="text-[11px] text-red-500 mt-2 truncate">
                {(itemsErr as Error)?.message || 'Request failed'}
              </p>
            ) : null}
          </div>
        </div>
      );
    }

    const customerType = cart.customerType || 'Retailer';
    const layout: PosLayoutMode = isMobilePos
      ? 'Image Grid View'
      : layoutOverride || (posSettings?.pos_screen_layout as PosLayoutMode) || 'Detail Card View';

    if (layout === 'Image Grid View') {
      return (
        <PosImageGridView
          items={gridItems}
          cartItems={cart.items}
          allowOos={allowOos}
          customerType={customerType}
          fulfillmentType={cart.fulfillmentType}
          promotions={promoCampaigns}
          onAddItem={handleAddItem}
          onOpenVariantModal={setVariantModalItemName}
          variant={isMobilePos ? 'mobileCashier' : 'default'}
        />
      );
    }

    if (layout === 'Compact List') {
      return (
        <PosCompactListView
          items={gridItems}
          cartItems={cart.items}
          allowOos={allowOos}
          customerType={customerType}
          fulfillmentType={cart.fulfillmentType}
          promotions={promoCampaigns}
          userId={userId}
          onAddItem={handleAddItem}
          onOpenVariantModal={setVariantModalItemName}
          onToggleFavorite={handleToggleFavorite}
        />
      );
    }

    return (
      <PosDetailCardView
        items={gridItems}
        cartItems={cart.items}
        allowOos={allowOos}
        posCustomFields={posCustomFields}
        customerType={customerType}
        fulfillmentType={cart.fulfillmentType}
        promotions={promoCampaigns}
        onAddItem={handleAddItem}
        onOpenVariantModal={setVariantModalItemName}
      />
    );
  }, [gridItems, isLoading, isHotSellingLoading, isHotSellingError, activeCategory, itemsError, itemsErr, viewMode, handleAddItem, handleToggleFavorite, allowOos, cart.items, cart.customerType, posSettings?.pos_screen_layout, layoutOverride, posCustomFields, posSyncMode, refetchItems, t, userId, qc, isMobilePos, promoCampaigns, favoriteIds]);

  return (
    <div className={`flex h-screen bg-gray-50 ${touchMode || isMobilePos ? 'pos-touch-mode' : ''}`}>
      <Sidebar />
      <ShiftGate>
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <SyncModeBanner />
          <TrialBanner />
          {returnNotice && (
            <div className="mx-4 mt-2 bg-amber-50 border border-amber-100 text-amber-800 text-sm px-4 py-2 rounded-lg">
              {returnNotice}
            </div>
          )}

          <div className="flex flex-1 overflow-hidden">
            <div className="flex-1 flex overflow-hidden min-w-0">
              {posSettings?.pos_category_position === 'Left' && (
                <div className="hidden lg:flex shrink-0 h-full">
                  <PosCategoryPanel
                    categories={categories}
                    activeCategory={activeCategory}
                    onSelect={setActiveCategory}
                    position="left"
                    viewMode={viewMode}
                  />
                </div>
              )}
              {/* Product Panel */}
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                <div className="bg-white border-b border-gray-200 p-2 space-y-1.5 lg:p-4 lg:space-y-3">
                  {/* Desktop toolbar — unchanged at lg+ */}
                  <div className="hidden lg:block space-y-3">
                    <div className="flex overflow-x-auto pb-1 -mb-1 hide-scrollbar items-center gap-2 shrink-0">
                      {!posSettings?.sell_only_products && (
                        <>
                          <button
                            onClick={() => { setViewMode('products'); setActiveCategory('All'); }}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${viewMode === 'products' ? 'bg-primary-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            <Package className="w-4 h-4" /> {t('pos.products')}
                          </button>
                          <button
                            onClick={() => { setViewMode('services'); setActiveCategory('All'); }}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${viewMode === 'services' ? 'bg-primary-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            <Wrench className="w-4 h-4" /> {t('pos.services')}
                          </button>
                        </>
                      )}
                      {canProcessReturns && (
                        <button
                          type="button"
                          onClick={() => setShowReturnModal(true)}
                          className="pos-toolbar-action gap-1.5"
                        >
                          <RotateCcw className="w-4 h-4" /> {t('pos.return')}
                          <span className="hidden lg:inline-flex"><ShortcutBadge binding={shortcutBindings.openReturn} /></span>
                        </button>
                      )}
                      {userCanAddExpenses && (
                        <button
                          type="button"
                          onClick={() => setShowExpenseModal(true)}
                          className="pos-toolbar-action gap-1.5"
                        >
                          <PakRupeeIcon className="w-4 h-4" /> {t('pos.expense')}
                          <span className="hidden lg:inline-flex"><ShortcutBadge binding={shortcutBindings.openExpense} /></span>
                        </button>
                      )}
                      <span className="relative inline-flex shrink-0">
                        <button
                          type="button"
                          onClick={() => setShowPendingModal(true)}
                          className="pos-toolbar-action gap-1.5"
                        >
                          <Clock className="w-4 h-4" /> {t('pos.pending')}
                          <span className="hidden lg:inline-flex"><ShortcutBadge binding={shortcutBindings.openPending} /></span>
                        </button>
                        {pendingInvoices.length > 0 && (
                          <span className="pos-toolbar-badge">
                            {pendingInvoices.length > 99 ? '99+' : pendingInvoices.length}
                          </span>
                        )}
                      </span>
                      {posSettings?.show_today_invoices_to_cashier && (
                        <button
                          type="button"
                          onClick={() => setShowTodaySalesModal(true)}
                          className="pos-toolbar-action gap-1.5"
                        >
                          <FileText className="w-4 h-4" /> {t('pos.todaySales')}
                          <span className="hidden lg:inline-flex"><ShortcutBadge binding={shortcutBindings.openTodaySales} /></span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          cart.addItem({
                            item: 'General Item',
                            item_name: 'General Item',
                            item_type: 'Product',
                            quantity: 1,
                            rate: 0,
                            amount: 0,
                          } as any);

                          setTimeout(() => {
                            const stateItems = useCartStore.getState().items;
                            const idx = stateItems.findIndex(i => i.item === 'General Item' && i.item_name === 'General Item');
                            if (idx >= 0) {
                              setEditingGeneralItemIdx(idx);
                            }
                          }, 0);
                        }}
                        className="pos-toolbar-action gap-1.5"
                      >
                        <Package className="w-4 h-4" /> {t('pos.generalItem', 'General Item')}
                      </button>
                      {!!posSettings?.customer_display_enabled && (
                        <button
                          type="button"
                          onClick={() => openCustomerDisplayWindow(company?.code)}
                          className="pos-toolbar-action"
                          title={t('pos.customerDisplayHint')}
                        >
                          <Monitor className="w-4 h-4" /> {t('pos.customerDisplay')}
                        </button>
                      )}
                      <div className="flex-1 min-w-[1rem]" />
                    </div>

                    <div className="max-w-3xl">
                      <form onSubmit={handleBarcodeSubmit} className="flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[140px]">
                          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <input
                            ref={desktopBarcodeInputRef}
                            type="text"
                            placeholder={t('pos.scanBarcode')}
                            className="input-field pl-9 pr-10 text-sm w-full border-primary-200 focus:border-primary-500"
                            value={barcodeScan}
                            onChange={(e) => {
                              setBarcodeScan(e.target.value);
                              setScanError('');
                            }}
                            disabled={scanPending}
                            autoComplete="off"
                            autoFocus={!isMobilePos}
                          />
                          <button
                            type="button"
                            onClick={() => setShowScanner(true)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-50 transition-colors"
                            title="Open Camera Scanner"
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          type="submit"
                          disabled={scanPending || !barcodeScan.trim()}
                          className="btn-primary text-sm px-4 py-2 shrink-0 whitespace-nowrap"
                        >
                          {scanPending ? '...' : t('common.add')}
                        </button>
                        <div className="flex items-center gap-1 shrink-0">
                          {(['Detail Card View', 'Image Grid View', 'Compact List'] as PosLayoutMode[]).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => setLayoutOverride(mode)}
                              className={`px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors ${(layoutOverride || posSettings?.pos_screen_layout || 'Detail Card View') === mode
                                  ? 'bg-primary-50 border-primary-300 text-primary-800'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                              {mode === 'Compact List' ? t('pos.layoutList') : mode === 'Image Grid View' ? t('pos.layoutGrid') : t('pos.layoutDetail')}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowShortcutsHelp(true)}
                          className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 shrink-0"
                          title={t('pos.shortcutsTitle', { key: formatShortcutKey(shortcutBindings.showShortcuts) })}
                        >
                          <Keyboard className="w-4 h-4" />
                          <span className="hidden sm:inline">{t('pos.shortcuts')}</span>
                        </button>
                        <div className="relative flex-1 min-w-[140px] sm:max-w-xs">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            ref={searchInputRef}
                            type="text" placeholder={t('common.search')}
                            className="input-field pl-9 text-sm w-full"
                            value={search} onChange={(e) => setSearch(e.target.value)}
                          />
                        </div>
                      </form>
                      {scanError && (
                        <p className="text-xs text-red-600 mt-1.5">{scanError}</p>
                      )}
                    </div>

                    <div className="hidden lg:block">
                      <PosQuickAccessStrip
                        favorites={favoriteProducts}
                        customerType={cart.customerType || 'Retailer'}
                        onSelect={handleAddItem}
                        onToggleFavorite={handleToggleFavorite}
                        isFavorite={(name) => favoriteIds.includes(name)}
                        showFavorites={showFavoritesStrip}
                        showEmptyHint={
                          (layoutOverride || posSettings?.pos_screen_layout || 'Detail Card View') === 'Compact List'
                        }
                      />
                    </div>

                    <div className="hidden lg:block">
                      {(!posSettings?.pos_category_position || posSettings.pos_category_position === 'Top') && (
                        <PosCategoryBar
                          categories={categories}
                          activeCategory={activeCategory}
                          onSelect={setActiveCategory}
                          position="top"
                          viewMode={viewMode}
                          className="border-t border-gray-100"
                        />
                      )}
                    </div>
                  </div>

                  {/* Mobile cashier-first toolbar — compact for product space */}
                  <div className="lg:hidden space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={toggleSidebar}
                        className="shrink-0 p-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
                        aria-label={t('common.menu', 'Menu')}
                      >
                        <Menu className="w-5 h-5" />
                      </button>
                      <form onSubmit={handleMobileScanSearchSubmit} className="flex-1 min-w-0">
                        <div className="relative">
                          <Barcode className="absolute start-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <input
                            ref={mobileBarcodeInputRef}
                            type="text"
                            inputMode="search"
                            enterKeyHint="search"
                            placeholder={t('pos.scanBarcodeOrSearch')}
                            className="input-field ps-9 pe-10 text-sm w-full min-h-[40px] h-10 border-primary-200 focus:border-primary-500 rounded-lg pos-mobile-scan-input"
                            value={mobileScanSearch}
                            onChange={(e) => {
                              setMobileScanSearch(e.target.value);
                              setScanError('');
                            }}
                            disabled={scanPending}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                            aria-label={t('pos.newSale')}
                            autoFocus={isMobilePos}
                          />
                          <button
                            type="button"
                            onClick={() => setShowScanner(true)}
                            className="absolute end-0.5 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title={t('pos.openCameraScanner', 'Open Camera Scanner')}
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                        </div>
                      </form>
                      <PosMobileOverflowMenu
                        canProcessReturns={canProcessReturns}
                        userCanAddExpenses={userCanAddExpenses}
                        showTodaySales={!!posSettings?.show_today_invoices_to_cashier}
                        showCustomerDisplay={!!posSettings?.customer_display_enabled}
                        sellOnlyProducts={!!posSettings?.sell_only_products}
                        viewMode={viewMode}
                        pendingCount={pendingInvoices.length}
                        heldCount={cart.holds.length}
                        onReturn={() => setShowReturnModal(true)}
                        onExpense={() => setShowExpenseModal(true)}
                        onPending={() => setShowPendingModal(true)}
                        onTodaySales={() => setShowTodaySalesModal(true)}
                        onCustomerDisplay={() => openCustomerDisplayWindow(company?.code)}
                        onViewModeChange={(mode) => { setViewMode(mode); setActiveCategory('All'); }}
                        onHeldCarts={handleToggleHeldCarts}
                        onGeneralItem={() => setEditingGeneralItemIdx(-1)}
                      />
                    </div>
                    {scanError && (
                      <p className="text-xs text-red-600 mt-1">{scanError}</p>
                    )}
                    <div className="relative" ref={mobileCategoryDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowMobileCategories((prev) => !prev)}
                        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors min-h-[48px]"
                      >
                        <span className="flex items-center gap-2 min-w-0 truncate">
                          {activeCategory === RECENT_CATEGORY && <Flame className="w-4 h-4 text-orange-500 shrink-0" />}
                          {activeCategory !== RECENT_CATEGORY && activeCategory !== 'All' && (() => {
                            const cat = categories.find((c: POSCategory) => c.name === activeCategory);
                            if (!cat) return null;
                            return (
                              <CategoryIcon
                                icon={cat.pos_category_icon}
                                image={cat.pos_category_image}
                                color={cat.pos_category_color || getDefaultCategoryColor(cat.category_name)}
                                size="sm"
                              />
                            );
                          })()}
                          <span className="truncate">{mobileActiveCategoryLabel}</span>
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showMobileCategories ? 'rotate-180' : ''}`} />
                      </button>
                      {showMobileCategories && (
                        <div className="absolute left-0 right-0 top-full mt-1.5 rounded-xl bg-white border border-gray-100 shadow-xl z-[100] max-h-64 overflow-y-auto p-1.5 space-y-0.5">
                          {viewMode === 'products' && (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveCategory(RECENT_CATEGORY);
                                setShowMobileCategories(false);
                              }}
                              className={`w-full text-left px-3 py-3 text-sm font-medium rounded-lg flex items-center gap-2 min-h-[44px] ${activeCategory === RECENT_CATEGORY
                                  ? `bg-primary-50 text-primary-700 font-semibold ${POS_CATEGORY_SELECTED_CLASS}`
                                  : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              <Flame className="w-4 h-4 shrink-0" />
                              {t('pos.hotSelling')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setActiveCategory('All');
                              setShowMobileCategories(false);
                            }}
                            className={`w-full text-left px-3 py-3 text-sm font-medium rounded-lg min-h-[44px] flex items-center gap-2 ${activeCategory === 'All'
                                ? `bg-primary-50 text-primary-700 font-semibold ${POS_CATEGORY_SELECTED_CLASS}`
                                : 'text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <LayoutGrid className="w-4 h-4 shrink-0" />
                            {t('common.all')}
                          </button>
                          {categories.map((cat: POSCategory) => {
                            const color = cat.pos_category_color || getDefaultCategoryColor(cat.category_name);
                            const selected = activeCategory === cat.name;
                            return (
                              <button
                                key={cat.name}
                                type="button"
                                onClick={() => {
                                  setActiveCategory(cat.name);
                                  setShowMobileCategories(false);
                                }}
                                className={`w-full text-left px-3 py-3 text-sm font-medium rounded-lg min-h-[48px] flex items-center gap-2 transition-colors text-white hover:opacity-90 ${selected ? POS_CATEGORY_SELECTED_CLASS : ''
                                  }`}
                                style={{ backgroundColor: color }}
                                aria-pressed={selected}
                              >
                                <CategoryIcon
                                  icon={cat.pos_category_icon}
                                  image={cat.pos_category_image}
                                  color="rgba(255,255,255,0.22)"
                                  foreground="#ffffff"
                                  size="sm"
                                />
                                <span className="truncate">{cat.category_name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-auto p-2 pb-24 lg:p-4 lg:pb-4">
                  {promoBanners.length > 0 && (
                    <div className="mb-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-800 text-sm px-3 py-2 font-medium">
                      {promoBanners[0]}
                    </div>
                  )}
                  {renderedItemsGrid}
                </div>
                <div className="hidden lg:block">
                  {posSettings?.pos_category_position === 'Bottom' && (
                    <PosCategoryBar
                      categories={categories}
                      activeCategory={activeCategory}
                      onSelect={setActiveCategory}
                      position="bottom"
                      viewMode={viewMode}
                    />
                  )}
                </div>
              </div>

              {posSettings?.pos_category_position === 'Right' && (
                <div className="hidden lg:flex shrink-0 h-full">
                  <PosCategoryPanel
                    categories={categories}
                    activeCategory={activeCategory}
                    onSelect={setActiveCategory}
                    position="right"
                    viewMode={viewMode}
                  />
                </div>
              )}
            </div>

            {/* Cart panel — slides from inline-end (RTL-safe) */}
            <div className={`
            fixed inset-y-0 end-0 z-50 w-full sm:w-[420px] sm:max-w-[420px] bg-white shadow-2xl flex flex-col transition-transform duration-300
            lg:relative lg:inset-auto lg:z-auto lg:shadow-none lg:border-s lg:border-gray-200 lg:!transform-none
            ${isCartMobileOpen ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full'}
          `}>
              {/* Mobile Close Button & Header */}
              <div className="lg:hidden p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" /> {t('pos.currentSale')}
                </h2>
                <div className="flex items-center gap-2">
                  {cart.holds.length > 0 && (
                    <HeldCartsToggleButton
                      count={cart.holds.length}
                      active={showHeldCarts}
                      shortcutBinding={formatShortcutKey(shortcutBindings.toggleHeldCarts)}
                      onClick={handleToggleHeldCarts}
                    />
                  )}
                  {cart.items.length > 0 && (
                    <>

                      <div className="flex items-center gap-2 mr-2 bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200" title={t('pos.repeatItemsTooltip', 'Toggle separate rows for repeated items')}>
                        <span className="text-[11px] font-semibold text-gray-600 hidden sm:block uppercase tracking-wider">{t('pos.repeatItems', 'Repeat Items')}</span>
                        <button
                          type="button"
                          onClick={() => cart.toggleRepeatItems()}
                          className={`${cart.repeatItems ? 'bg-primary-600' : 'bg-gray-300'
                            } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                        >
                          <span
                            className={`${cart.repeatItems ? 'translate-x-4' : 'translate-x-0'
                              } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                          />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearCart}
                        className="pos-toolbar-action text-red-900 hover:text-red-700 min-h-[44px]"
                      >
                        {t('pos.clearCart')}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setIsCartMobileOpen(false)}
                    className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 border-b border-gray-200">
                <div className="hidden lg:flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" /> {t('pos.currentSale')}
                  </h2>
                  <div className="flex items-center gap-2">
                    {cart.holds.length > 0 && (
                      <HeldCartsToggleButton
                        count={cart.holds.length}
                        active={showHeldCarts}
                        shortcutBinding={formatShortcutKey(shortcutBindings.toggleHeldCarts)}
                        onClick={handleToggleHeldCarts}
                      />
                    )}
                    {cart.items.length > 0 && (
                      <>

                        <div className="flex items-center gap-2 mr-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200" title={t('pos.repeatItemsTooltip', 'Toggle separate rows for repeated items')}>
                          <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('pos.repeatItems', 'Repeat Items')}</span>
                          <button
                            type="button"
                            onClick={() => cart.toggleRepeatItems()}
                            className={`${cart.repeatItems ? 'bg-primary-600' : 'bg-gray-300'
                              } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                          >
                            <span
                              className={`${cart.repeatItems ? 'translate-x-4' : 'translate-x-0'
                                } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                            />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearCart}
                          className="pos-toolbar-action text-red-900 hover:text-red-700"
                        >
                          {t('pos.clearCart')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {cart.items.length > 0 && (
                  <p className="hidden lg:block text-[10px] text-gray-500 mb-2 leading-relaxed">
                    {t('pos.cartShortcutHint', {
                      del: formatShortcutKey(shortcutBindings.removeCartLine),
                      up: formatShortcutKey(shortcutBindings.prevCartLine),
                      down: formatShortcutKey(shortcutBindings.nextCartLine),
                      plus: formatShortcutKey(shortcutBindings.increaseCartQty),
                      minus: formatShortcutKey(shortcutBindings.decreaseCartQty),
                    })}
                  </p>
                )}

                {/* Fulfillment Option (Pickup / Delivery) */}
                {posSettings?.enable_delivery && (
                  <div className="mb-2 p-2 bg-gray-50/80 border border-gray-200/70 rounded-xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                        {cart.fulfillmentType === 'Delivery' ? (
                          <Truck className="w-3.5 h-3.5 text-blue-600" />
                        ) : (
                          <Store className="w-3.5 h-3.5 text-gray-500" />
                        )}
                        Order Type
                      </span>
                      <div className="inline-flex rounded-lg bg-gray-200/70 p-0.5 text-xs">
                        <button
                          type="button"
                          onClick={() => handleFulfillmentChange('Pickup')}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 font-semibold rounded-md transition-all ${
                            cart.fulfillmentType === 'Pickup'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Store className="w-3.5 h-3.5" />
                          Pickup
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFulfillmentChange('Delivery')}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 font-semibold rounded-md transition-all ${
                            cart.fulfillmentType === 'Delivery'
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Truck className="w-3.5 h-3.5" />
                          Delivery
                        </button>
                      </div>
                    </div>

                    {cart.fulfillmentType === 'Delivery' && (
                      <div className="pt-1 border-t border-gray-200/60">
                        {cart.deliveryInfo?.address ? (
                          <div className="flex items-center justify-between bg-white border border-blue-200 rounded-lg p-2 text-xs">
                            <div className="flex items-start gap-1.5 min-w-0 pr-2">
                              <MapPin className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">
                                  {cart.deliveryInfo.recipient_name || selectedCustomer.name || 'Delivery Customer'}
                                  {cart.deliveryInfo.phone ? ` • ${cart.deliveryInfo.phone}` : ''}
                                  {posSettings?.delivery_pricing_mode === 'runtime_charge' && (
                                    <span className="text-amber-800 font-bold ml-1">
                                      • Fee: {formatMoney(deliveryCharge)}
                                    </span>
                                  )}
                                </p>
                                <p className="text-gray-600 truncate text-[11px]">
                                  {cart.deliveryInfo.address}
                                  {cart.deliveryInfo.postal_code ? ` (${cart.deliveryInfo.postal_code})` : ''}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowDeliveryModal(true)}
                              className="shrink-0 px-2 py-1 text-[11px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                            >
                              Edit
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-amber-50 border border-amber-200/80 rounded-lg p-2 text-xs">
                            <span className="text-amber-800 text-[11px] font-medium flex items-center gap-1">
                              <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                              Address required for delivery
                            </span>
                            <button
                              type="button"
                              onClick={() => setShowDeliveryModal(true)}
                              className="shrink-0 px-2 py-1 text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors shadow-xs"
                            >
                              Add Address
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <CustomerFields
                  customers={customers}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  onCreateCustomer={handleCreateCustomer}
                  isCreatingCustomer={createCustomerMutation.isPending}
                />
                {saleError && !showPayment && (
                  <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {saleError}
                  </p>
                )}
                {showHeldCarts && cart.holds.length > 0 && (
                  <HeldCartsPanel
                    holds={cart.holds}
                    holdTotal={(h) => holdCartTotal(h, pricesIncludeTax)}
                    formatTime={formatHoldTime}
                    onResume={handleResumeHold}
                    onDiscard={handleDiscardHold}
                  />
                )}
              </div>

              <div className="flex-1 overflow-auto p-3">
                {cart.items.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm text-center">
                    <div>
                      <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-20" />
                      <p>{t('pos.addItems')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {cart.items.map((item, idx) => {
                      const isSelected = selectedCartLineIdx === idx;
                      const promoLine = promoEval.lines[idx];
                      return (
                        <div
                          key={`${item.item}-${item.variant || ''}-${idx}`}
                          ref={(el) => { cartLineRefs.current[idx] = el; }}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedCartLineIdx(idx)}
                          onDoubleClick={() => {
                            if (item.item === 'General Item') {
                              setEditingGeneralItemIdx(idx);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedCartLineIdx(idx);
                            }
                          }}
                          className={`pos-cart-line rounded-lg border p-2 cursor-pointer transition-all ${isSelected
                              ? 'border-primary-500 bg-primary-50/60'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80'
                            }`}
                        >
                          <div className="flex items-start justify-between gap-1.5 mb-1.5">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-1.5 mb-1">
                                <p className={`font-semibold text-xs leading-snug line-clamp-2 flex-1 min-w-0 ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                  {item.item_name}
                                  {item.variant_name && (
                                    <span className={`font-medium ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
                                      {' '}— {item.variant_name}
                                    </span>
                                  )}
                                  {Boolean(item.has_age_restriction) && (item.minimum_age ?? 0) > 0 && (
                                    <span className="inline-flex items-center ml-1.5 px-1 py-0.2 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200" title={`Age Restricted: ${item.minimum_age}+`}>
                                      {item.minimum_age}+
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLineNoteEdit({ index: idx, mode: 'comment' });
                                    }}
                                    className={`p-1 rounded ${item.line_comment ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                                    title={item.line_comment ? 'Edit line comment' : 'Add line comment'}
                                  >
                                    <MessageSquare className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (item.item === 'General Item') {
                                        setEditingGeneralItemIdx(idx);
                                      } else {
                                        setLineNoteEdit({ index: idx, mode: 'rename' });
                                      }
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                                    title={item.item === 'General Item' ? 'Edit General Item details' : 'Rename for this invoice'}
                                  >
                                    {item.item === 'General Item' ? <Edit2 className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </div>
                              {item.line_comment ? (
                                <p className="text-[10px] text-slate-600 italic mt-0.5 line-clamp-2">
                                  {item.line_comment}
                                </p>
                              ) : null}
                              {promoLine?.hint && (
                                <p className="text-[10px] text-rose-600 mt-0.5">{promoLine.hint}</p>
                              )}
                              {promoLine && promoLine.promo_amount > 0 && (
                                <p className="text-[10px] text-emerald-700 mt-0.5">
                                  {t('promotions.lineSaving', { amount: formatMoney(promoLine.promo_amount) })}
                                </p>
                              )}
                              {(item.batch_no || item.is_weighted || item.imei) && (
                                <p className="text-[9px] text-gray-500 mt-0.5 line-clamp-1">
                                  {item.is_weighted && `${Number(item.quantity).toFixed(3)} ${item.unit_of_measurement || 'kg'} × ${formatMoney(item.rate)}`}
                                  {item.batch_no && `${item.is_weighted ? ' · ' : ''}${t('pos.batch')}: ${item.batch_no}`}
                                  {item.expiry_date && ` · ${t('pos.expiry')}: ${item.expiry_date}`}
                                  {item.imei && `${(item.is_weighted || item.batch_no) ? ' · ' : ''}${t('pos.imei')}: ${item.imei}`}
                                  {item.mac_address && ` · MAC: ${item.mac_address}`}
                                </p>
                              )}
                              {item.track_serials && (
                                <div className="mt-1.5 inline-flex">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSerialModalItemIdx(idx);
                                    }}
                                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium border ${
                                      item.imei || item.mac_address
                                        ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                                        : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                    }`}
                                  >
                                    <Hash className="w-3 h-3" />
                                    {item.imei || item.mac_address 
                                      ? `Serial: ${item.imei || item.mac_address}` 
                                      : 'Select Serial No.'}
                                  </button>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                cart.removeItem(idx);
                              }}
                              className="pos-cart-delete text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded-md transition-colors shrink-0"
                              title={formatShortcutKey(shortcutBindings.removeCartLine)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div
                            className="flex items-center gap-1.5 min-w-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                type="button"
                                disabled={!!item.imei || !!item.track_serials}
                                onClick={() => handleUpdateQuantity(idx, item.quantity - (item.is_weighted ? 0.01 : 1), true)}
                                className="pos-cart-qty-btn w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                                title={formatShortcutKey(shortcutBindings.decreaseCartQty)}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                step={item.is_weighted || item.allow_fractional_qty ? '0.01' : '1'}
                                value={item.quantity}
                                disabled={!!item.imei || !!item.track_serials}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const val = raw === '' ? 0 : parseFloat(raw);
                                  handleUpdateQuantity(idx, val, false);
                                }}
                                onBlur={() => {
                                  if (item.quantity <= 0) {
                                    if (confirm('Remove item from cart?')) {
                                      cart.removeItem(idx);
                                    } else {
                                      handleUpdateQuantity(idx, item.is_weighted ? 0.01 : 1, false);
                                    }
                                  }
                                }}
                                className={`${item.is_weighted || item.allow_fractional_qty ? 'w-11' : 'w-9'
                                  } min-w-0 px-0.5 text-center text-xs font-semibold text-gray-900 border border-gray-200 rounded-md py-1 bg-white focus:ring-1 focus:ring-primary-400 focus:border-primary-400 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              />
                              <button
                                type="button"
                                disabled={!!item.imei || !!item.track_serials}
                                onClick={() => handleUpdateQuantity(idx, item.quantity + (item.is_weighted ? 0.01 : 1), false)}
                                className="pos-cart-qty-btn w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                                title={formatShortcutKey(shortcutBindings.increaseCartQty)}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <input
                              type="number"
                              min={item.customer_custom_price ? 0 : (item.min_rate ?? 0)}
                              step="0.01"
                              value={item.rate === 0 ? '' : item.rate}
                              disabled={!userCanEditRate}
                              readOnly={!userCanEditRate}
                              onChange={(e) => handleUpdateRateChange(idx, e.target.value)}
                              onBlur={() => handleValidateRate(idx)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleValidateRate(idx);
                              }}
                              title={
                                !userCanEditRate
                                  ? 'Sale price is fixed for your role'
                                  : item.customer_custom_price
                                    ? `Customer agreed price: ${formatMoney(item.customer_custom_price)}`
                                    : item.min_rate
                                      ? `Min rate: ${formatMoney(item.min_rate)}`
                                      : undefined
                              }
                              className={`w-[4.75rem] min-w-0 shrink px-1.5 py-1 text-right text-xs font-medium text-gray-900 border border-gray-200 rounded-md outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${userCanEditRate ? 'bg-white' : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                                }`}
                            />
                            <span className="flex-1 text-end text-sm font-bold text-gray-900 tabular-nums truncate min-w-0">
                              {promoLine && promoLine.promo_amount > 0 ? (
                                <>
                                  <span className="line-through text-gray-400 font-medium text-[10px] me-1">
                                    {formatMoney(item.amount)}
                                  </span>
                                  {formatMoney(promoLine.charged_amount)}
                                </>
                              ) : (
                                formatMoney(item.amount)
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {(cart.items.length > 0 || cart.holds.length > 0) && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {cart.items.length > 0 && (
                    <>
                      <div className="space-y-1.5 text-sm mb-3">
                        <div className="flex justify-between text-gray-500">
                          <span>{t('common.subtotal')}</span>
                          <span>{formatMoney(subtotal)}</span>
                        </div>
                        {promoEval.total_saving > 0 && (
                          <div className="flex justify-between text-emerald-700">
                            <span>{t('promotions.billLabel')}</span>
                            <span>− {formatMoney(promoEval.total_saving)}</span>
                          </div>
                        )}
                        {(!posSettings || posSettings.show_discount_field_in_pos !== false) && (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">{t('common.discount')}</span>
                              <div className="flex items-center border border-gray-200 rounded overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDiscountType(prev => prev === 'amount' ? 'percentage' : 'amount');
                                    setDiscountInput('');
                                    setSaleError('');
                                  }}
                                  className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium border-r border-gray-200 transition-colors"
                                >
                                  {discountType === 'amount' ? getCurrencySymbol() : '%'}
                                </button>
                                <input
                                  type="number" min={0}
                                  className="w-20 text-right text-sm px-2 py-1 outline-none focus:bg-gray-50"
                                  value={discountInput}
                                  onChange={(e) => calculateAndSetDiscount(e.target.value, discountType, billedSubtotal)}
                                  placeholder="0"
                                />
                              </div>
                              <span className="flex-1" />
                              <span className="text-gray-500">{formatMoney(cart.discount)}</span>
                            </div>
                            {!canBypassStaffLimits(role) && maxDiscountPct > 0 && (
                              <p className="text-[11px] text-gray-400">
                                Your discount limit: {maxDiscountPct}%
                                {managerApprover ? ' · Manager approved' : ''}
                              </p>
                            )}
                          </>
                        )}
                        {(enableItemTax || !posSettings || posSettings.show_tax_field_in_pos !== false) && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{taxFieldLabel}</span>
                            {enableItemTax ? (
                              <>
                                <span className="flex-1 text-xs text-gray-400">{t('pos.autoFromItem')}</span>
                                <span className="text-gray-700 font-medium">{formatMoney(cart.tax)}</span>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden w-28">
                                  <button
                                    onClick={() => {
                                      setTaxType(taxType === 'amount' ? 'percentage' : 'amount');
                                    }}
                                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium border-r border-gray-200 transition-colors"
                                  >
                                    {taxType === 'amount' ? getCurrencySymbol() : '%'}
                                  </button>
                                  <input
                                    type="number" min={0}
                                    className="w-20 text-right text-sm px-2 py-1 outline-none focus:bg-gray-50"
                                    value={taxInput}
                                    onChange={(e) => calculateAndSetTax(e.target.value, taxType, subtotal, cart.discount, pricesIncludeTax)}
                                    placeholder="0"
                                  />
                                </div>
                                <span className="flex-1" />
                                <span className="text-gray-500">{formatMoney(cart.tax)}</span>
                              </>
                            )}
                          </div>
                        )}
                        {posSettings?.enable_delivery && cart.fulfillmentType === 'Delivery' && posSettings?.delivery_pricing_mode === 'runtime_charge' && (
                          <div className="flex justify-between items-center text-xs py-1 text-gray-700 bg-amber-50/60 -mx-1 px-1.5 rounded-lg border border-amber-100">
                            <span className="flex items-center gap-1.5 font-medium text-amber-900">
                              <Truck className="w-3.5 h-3.5 text-amber-600" />
                              Delivery Fee
                            </span>
                            <button
                              type="button"
                              onClick={() => setShowDeliveryModal(true)}
                              className="font-bold text-blue-600 hover:underline flex items-center gap-1"
                              title="Click to edit delivery fee"
                            >
                              <span>{formatMoney(deliveryCharge)}</span>
                              <span className="text-[10px] text-gray-400">✎</span>
                            </button>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t border-gray-200">
                          <span>{t('common.total')}</span>
                          <span>{formatMoney(netTotal)}</span>
                        </div>
                        <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                          <span className="text-xs text-gray-500 line-clamp-1 flex-1 mr-2 italic">{cart.notes || ''}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 mb-2">
                        {isQuotationMode && (
                          <div className="flex flex-col w-full gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1 ml-1">{t('pos.expiryDate', 'Expiry Date')}</label>
                              <input
                                type="date"
                                value={quoteExpiryDate}
                                onChange={e => setQuoteExpiryDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                                title="Expiry Date"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleSaveQuote}
                              disabled={cart.items.length === 0}
                              title="Save Quote"
                              className="btn-primary w-full py-3 text-base flex items-center justify-center gap-2 disabled:opacity-50 text-white font-medium"
                            >
                              <FileText className="w-5 h-5" />
                              <span>Quote</span>
                            </button>
                          </div>
                        )}
                        
                        {!isQuotationMode && (
                          <button
                            type="button"
                            onClick={handleHoldCart}
                            disabled={cart.items.length === 0 || cart.holds.length >= MAX_HELD_CARTS}
                            title={cart.holds.length >= MAX_HELD_CARTS ? `Maximum ${MAX_HELD_CARTS} held carts` : `Hold current sale (${formatShortcutKey(shortcutBindings.holdCart)})`}
                            className="flex-1 py-3 text-base flex flex-col items-center justify-center gap-1 rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 font-medium"
                          >
                            <Pause className="w-4 h-4 text-gray-700" />
                            <span className="text-xs">{t('pos.hold')}</span>
                          </button>
                        )}

                        {!isQuotationMode && (
                          <button
                            onClick={handleCheckout}
                            disabled={cart.items.length === 0 || invoiceLimits?.can_add_more === false}
                            className="btn-primary flex-[1.4] py-3 text-base flex items-center justify-center gap-2 disabled:opacity-50 text-white"
                            title={`Checkout (${formatShortcutKey(shortcutBindings.openPay)})`}
                          >
                            <CreditCard className="w-5 h-5" />
                            <span>{t('pos.pay')} {formatMoney(netTotal)}</span>
                            <ShortcutBadge binding={shortcutBindings.openPay} variant="primary" />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {cart.items.length === 0 && cart.holds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleToggleHeldCarts}
                      className="pos-held-toggle w-full min-h-[52px] text-base flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 font-semibold"
                    >
                      <Layers className="w-5 h-5" />
                      {t('pos.heldCount', { count: cart.holds.length })}
                    </button>
                  )}
                  {invoiceLimits && invoiceLimits.max != null && cart.items.length > 0 && (
                    <p className="text-xs text-gray-400 text-center mt-2">
                      Invoices this month: {invoiceLimits.current} / {invoiceLimits.max}
                    </p>
                  )}
                  {invoiceLimits?.can_add_more === false && cart.items.length > 0 && (
                    <p className="text-xs text-amber-600 text-center mt-1">
                      Monthly invoice limit reached. Upgrade to continue billing.
                    </p>
                  )}
                </div>
              )}
            </div>

            <PosMobileCheckoutBar
              itemCount={cart.items.reduce((sum, item) => sum + item.quantity, 0)}
              total={netTotal}
              heldCount={cart.holds.length}
              onViewCart={() => setIsCartMobileOpen(true)}
              onCheckout={() => setIsCartMobileOpen(true)}
              onHeldCarts={handleToggleHeldCarts}
            />
          </div>

          {weightPending && (
            <WeightEntryModal
              itemName={weightPending.item.item_name}
              ratePerUnit={weightPending.rate}
              uom={weightPending.variant?.unit_of_measurement || weightPending.item.unit_of_measurement || 'Kilogram'}
              onCancel={() => setWeightPending(null)}
              onConfirm={(kg) => {
                const pending = weightPending;
                setWeightPending(null);
                handleAddItem(pending.item, pending.variant, kg);
              }}
            />
          )}

          <UnknownBarcodeModal
            open={!!unknownBarcode}
            barcode={unknownBarcode || ''}
            country={shopCountry}
            role={role}
            permissions={permissions}
            onClose={() => {
              setUnknownBarcode(null);
              focusBarcodeField();
            }}
            onSave={handleUnknownBarcodeSave}
          />

          {agePending && (
            <AgeVerificationModal
              isOpen={Boolean(agePending)}
              itemName={agePending.variant ? `${agePending.item.item_name} (${agePending.variant.variant_name})` : agePending.item.item_name}
              minimumAge={Number(agePending.item.minimum_age) || 18}
              onCancel={handleAgeCancel}
              onConfirm={handleAgeConfirm}
            />
          )}

          {serialPending && (
            <SerialSelectModal
              open
              itemName={serialPending.item.name}
              itemLabel={serialPending.item.item_name}
              variant={serialPending.variant?.name}
              excludeImeis={cart.items.map((c) => c.imei).filter(Boolean) as string[]}
              onClose={() => setSerialPending(null)}
              onSelect={(serial) => {
                const pending = serialPending;
                setSerialPending(null);
                handleAddItem(pending.item, pending.variant, 1, {
                  serial: serial.name,
                  imei: serial.imei,
                  mac_address: serial.mac_address,
                });
              }}
            />
          )}

          {serialModalItemIdx !== null && cart.items[serialModalItemIdx] && (
            <SerialSelectModal
              open
              itemName={cart.items[serialModalItemIdx].item}
              itemLabel={cart.items[serialModalItemIdx].item_name}
              variant={cart.items[serialModalItemIdx].variant_name}
              excludeImeis={cart.items.map((c) => c.imei).filter(Boolean) as string[]}
              onClose={() => setSerialModalItemIdx(null)}
              onSelect={(serial) => {
                cart.updateSerial(
                  serialModalItemIdx,
                  serial.name,
                  serial.imei,
                  serial.mac_address
                );
                setSerialModalItemIdx(null);
              }}
            />
          )}

          {showServiceCheckout && (
            <ServiceCheckoutModal
              open={showServiceCheckout}
              lines={cart.items}
              onCancel={() => setShowServiceCheckout(false)}
              onConfirm={handleServiceCheckoutConfirm}
            />
          )}

          {showDeliveryModal && (
            <DeliveryDetailsModal
              open={showDeliveryModal}
              initialData={cart.deliveryInfo}
              customerName={selectedCustomer.name && selectedCustomer.name !== WALK_IN_CUSTOMER_ID ? selectedCustomer.name : ''}
              customerPhone={selectedCustomer.phone}
              customerAddress={selectedCustomer.address || undefined}
              customerPostalCode={selectedCustomer.postal_code || undefined}
              isWalkIn={!selectedCustomer.id || selectedCustomer.name === WALK_IN_CUSTOMER_ID || Boolean(selectedCustomer.is_walk_in)}
              askDeliveryPrice={posSettings?.delivery_pricing_mode === 'runtime_charge'}
              onClose={() => setShowDeliveryModal(false)}
              onSave={(info) => cart.setDeliveryInfo(info)}
            />
          )}

          {showPayment && (
            <PaymentModal
              ref={paymentModalRef}
              netTotal={netTotal}
              customerName={selectedCustomer.name && selectedCustomer.id ? selectedCustomer.name : t('pos.walkIn')}
              onComplete={handleCompleteSale}
              onCancel={() => { setShowPayment(false); setSaleError(''); focusBarcodeField(); }}
              isLoading={createInvoiceMutation.isPending}
              shiftPocketName={openShift?.pocket_name}
              maxWriteOff={posSettings?.max_round_off ?? 50}
              allowCreditSale={allowCreditSale}
              externalError={saleError}
              enableCommission={!!posSettings?.enable_commission}
            // BACKUP: allowCreditSale={!isWalkInCustomer}
            />
          )}

          {isLocked && <LockScreenModal onUnlock={() => setLocked(false)} />}

          <ManagerOverrideModal
            open={showManagerOverride}
            onClose={() => {
              setShowManagerOverride(false);
              if (pendingRateIdxRef.current != null && pendingRateRef.current != null) {
                const idx = pendingRateIdxRef.current;
                const line = cart.items[idx];
                if (line?.min_rate && line.min_rate > 0) {
                  cart.updateRate(idx, line.min_rate);
                }
              }
              // SD-UK-052: drop rejected over-cap input so billedSubtotal effect cannot reopen modal
              if (pendingDiscountRef.current != null) {
                setDiscountInput(cart.discount ? String(cart.discount) : '');
              }
              pendingDiscountRef.current = null;
              pendingDiscountInputRef.current = '';
              pendingRateIdxRef.current = null;
              pendingRateRef.current = null;
            }}
            onApproved={(approverId) => {
              setManagerApprover(approverId);
              if (pendingDiscountRef.current != null) {
                cart.setDiscount(pendingDiscountRef.current);
                if (pendingDiscountInputRef.current) {
                  setDiscountInput(pendingDiscountInputRef.current);
                }
                pendingDiscountRef.current = null;
                pendingDiscountInputRef.current = '';
                setSaleError('');
              }
              if (pendingRateIdxRef.current != null && pendingRateRef.current != null) {
                cart.updateRate(pendingRateIdxRef.current, pendingRateRef.current);
                pendingRateIdxRef.current = null;
                pendingRateRef.current = null;
                setSaleError('');
              }
            }}
            message={overrideMessage}
          />

          <PosShortcutsHelpModal open={showShortcutsHelp} onClose={() => setShowShortcutsHelp(false)} />

          {showReceipt && lastInvoice && (
            <Receipt
              invoiceName={lastInvoice}
              previewInvoice={receiptPreview || undefined}
              onClose={() => {
                setShowReceipt(false);
                setReceiptPreview(null);
                focusBarcodeField();
              }}
            />
          )}

          {showReturnModal && (
            <CustomerReturnModal
              openShiftName={openShift?.name}
              initialInvoice={returnInvoiceId}
              onClose={() => {
                setShowReturnModal(false);
                setReturnInvoiceId(undefined);
              }}
              onSuccess={(result) => {
                setReturnNotice(
                  `Return ${result.name} processed. Cash refund: ${formatMoney(result.refund_amount)}`,
                );
                setTimeout(() => setReturnNotice(''), 8000);
                qc.invalidateQueries({ queryKey: ['posItems'] });
                qc.invalidateQueries({ queryKey: ['dailySummary'] });
              }}
            />
          )}

          {showExpenseModal && (
            <AddExpenseModal onClose={() => setShowExpenseModal(false)} />
          )}

          {variantModalItem && (
            <VariantSelectModal
              item={variantModalItem}
              cartItems={cart.items}
              allowOos={allowOos}
              onSelect={(variant) => handleAddItem(variantModalItem, variant)}
              onClose={() => setVariantModalItemName(null)}
            />
          )}

          <PendingInvoicesModal
            isOpen={showPendingModal}
            onClose={() => setShowPendingModal(false)}
            maxWriteOff={posSettings?.max_round_off ?? 50}
          />

          <TodaySalesModal
            isOpen={showTodaySalesModal}
            onClose={() => setShowTodaySalesModal(false)}
            canReturn={canProcessReturns}
            onReturnInvoice={canProcessReturns ? (invName) => {
              setShowTodaySalesModal(false);
              setReturnInvoiceId(invName);
              setShowReturnModal(true);
            } : undefined}
          />

          {showAddCustomerModal && (
            <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
              <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[95vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Add New Customer</h3>
                  <button onClick={() => setShowAddCustomerModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4 text-left">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className={`input-field ${newCustomerErrors.name ? 'border-red-400 focus:ring-red-300' : ''}`}
                      value={newCustomerFormData.customer_name}
                      onChange={(e) => {
                        setNewCustomerErrors((prev) => ({ ...prev, name: undefined }));
                        setNewCustomerFormData(prev => ({ ...prev, customer_name: e.target.value }));
                      }}
                      placeholder="e.g. John Doe"
                    />
                    {newCustomerErrors.name && (
                      <p className="text-xs text-red-500 mt-1">{newCustomerErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      className="input-field bg-gray-50 text-gray-700 cursor-not-allowed"
                      value={COUNTRIES.find((c) => c.code === defaultCountry)?.name || defaultCountry}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Province / State / Region</label>
                      <select
                        className="input-field"
                        value={newCustomerFormData.province}
                        onChange={(e) => setNewCustomerFormData(prev => ({ ...prev, province: e.target.value }))}
                      >
                        <option value="">Select…</option>
                        {getRegionsForCountry(defaultCountry).map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {defaultCountry === 'US' ? 'ZIP Code' : 'Postal Code'}
                      </label>
                      {getCountry(defaultCountry).usePostcodeLookup ? (
                        <UkPostcodeLookup
                          postalCode={newCustomerFormData.postal_code}
                          onPostalCodeChange={(v) => {
                            setNewCustomerPostcodeError('');
                            setNewCustomerFormData((prev) => ({ ...prev, postal_code: v }));
                          }}
                          onFill={(fields) => {
                            setNewCustomerPostcodeError('');
                            setNewCustomerFormData((prev) => ({
                              ...prev,
                              country: defaultCountry,
                              postal_code: fields.postal_code,
                              province: fields.province || prev.province,
                              city: fields.city || prev.city,
                              address: fields.address !== undefined ? fields.address : prev.address,
                            }));
                          }}
                        />
                      ) : (
                        <input
                          className="input-field"
                          value={newCustomerFormData.postal_code}
                          onChange={(e) => {
                            setNewCustomerPostcodeError('');
                            setNewCustomerFormData((prev) => ({ ...prev, postal_code: e.target.value }));
                          }}
                        />
                      )}
                      {newCustomerPostcodeError && (
                        <p className="text-xs text-red-500 mt-1">{newCustomerPostcodeError}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    {getCountry(defaultCountry).useCityMaster ? (
                      <select
                        className="input-field"
                        value={newCustomerFormData.city}
                        onChange={(e) => setNewCustomerFormData(prev => ({ ...prev, city: e.target.value }))}
                      >
                        <option value="">Select a city...</option>
                        {cities.map((c) => (
                          <option key={c.name} value={c.name}>{c.city}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input-field"
                        value={newCustomerFormData.city}
                        onChange={(e) => setNewCustomerFormData(prev => ({ ...prev, city: e.target.value }))}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <textarea
                      rows={2}
                      className="input-field"
                      value={newCustomerFormData.address}
                      onChange={(e) => setNewCustomerFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="e.g. 10 Downing Street, Flat 2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={isPakistan(defaultCountry) ? 11 : 15}
                      className={`input-field ${newCustomerErrors.mobile ? 'border-red-400 focus:ring-red-300' : ''}`}
                      value={newCustomerFormData.mobile}
                      onChange={(e) => {
                        setNewCustomerErrors((prev) => ({ ...prev, mobile: undefined }));
                        setNewCustomerFormData(prev => ({
                          ...prev,
                          country: defaultCountry,
                          mobile: sanitizePhoneInput(e.target.value, defaultCountry),
                        }));
                      }}
                      placeholder={isPakistan(defaultCountry) ? 'e.g. 03001234567' : 'Phone number'}
                    />
                    {newCustomerErrors.mobile && (
                      <p className="text-xs text-red-500 mt-1">{newCustomerErrors.mobile}</p>
                    )}
                  </div>
                </div>
                <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddCustomerModal(false);
                      setNewCustomerErrors({});
                      setNewCustomerPostcodeError('');
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const nextErrors: { name?: string; mobile?: string } = {};
                      if (!newCustomerFormData.customer_name.trim()) {
                        nextErrors.name = 'Customer name is required.';
                      }
                      const mobileErr = validatePhone(newCustomerFormData.mobile, defaultCountry, { required: true });
                      if (mobileErr) {
                        nextErrors.mobile = mobileErr;
                      }
                      if (nextErrors.name || nextErrors.mobile) {
                        setNewCustomerErrors(nextErrors);
                        return;
                      }
                      createCustomerMutation.mutate({
                        customer_name: newCustomerFormData.customer_name.trim(),
                        mobile: normalizePhone(newCustomerFormData.mobile, defaultCountry),
                        country: defaultCountry,
                        province: newCustomerFormData.province,
                        postal_code: newCustomerFormData.postal_code,
                        city: newCustomerFormData.city,
                        address: newCustomerFormData.address.trim(),
                        customer_type: 'Retailer',
                        _action: 'create',
                      });
                    }}
                    disabled={createCustomerMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {createCustomerMutation.isPending ? 'Saving...' : 'Save Customer'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {showScanner && (
            <CameraScannerModal
              onScan={handleCameraScan}
              onClose={() => {
                setShowScanner(false);
                setScanError('');
              }}
              scanPending={scanPending}
              scanError={scanError}
            />
          )}
        </div>
      </ShiftGate>

      <GeneralItemModal
        isOpen={editingGeneralItemIdx !== null}
        onClose={() => setEditingGeneralItemIdx(null)}
        cartItem={editingGeneralItemIdx !== null ? cart.items[editingGeneralItemIdx] : null}
        onSave={(name, cost, sale, isService) => {
          if (editingGeneralItemIdx !== null) {
            cart.updateGeneralItem(editingGeneralItemIdx, name, cost, sale, isService);
            setEditingGeneralItemIdx(null);
          }
        }}
      />
      {lineNoteEdit != null && cart.items[lineNoteEdit.index] && (
        <CartLineNoteModal
          mode={lineNoteEdit.mode}
          itemName={cart.items[lineNoteEdit.index].item_name}
          initialValue={
            lineNoteEdit.mode === 'rename'
              ? cart.items[lineNoteEdit.index].item_name
              : cart.items[lineNoteEdit.index].line_comment || ''
          }
          onClose={() => setLineNoteEdit(null)}
          onSave={(value) => {
            if (lineNoteEdit.mode === 'rename') {
              cart.updateItemName(lineNoteEdit.index, value);
            } else {
              cart.updateLineComment(lineNoteEdit.index, value);
            }
          }}
        />
      )}
    </div>
  );
}